import type { ApprovalRef } from '../../generated/executionApprovals'
import { toast } from 'sonner'
import { ActionCard } from '../chat/ActionCard'
import type { DirectorSkillSelection } from '../chat/directorSkillSelection'
import { isSchedulingQuestionKey as isSchedulingQuestionId } from "../../utils/schedulingQuestions"
/**
 * RFC 0036: Agent Chat Component
 *
 * A chat interface for interacting with the backend orchestrator.
 * Reuses existing chat components (ChatMessage, ChatInput, QuestionCard).
 *
 * Features:
 * - Status-aware send button (idle/running/polling/pending_questions)
 * - Dismissable error card above input
 * - Question option cards for user responses
 */

import { memo, useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback, isValidElement } from 'react'
import { useTranslation } from 'react-i18next'
import { APP_NAME } from '../../constants/brand'
import { normalizeLocale } from '../../i18n/supportedLocales'
import Markdown from 'react-markdown'
import { Pencil, WandSparkles } from 'lucide-react'
import { createDebug, createNadDebug } from '../../utils/debugLog'
import { type ReactFlowCanvasEdge, type ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import remarkGfm from 'remark-gfm'
import { listenAppEvent } from '../canvas-v2/nodes/shared/useNodeActions'
import {
  useAgentChat,
  type ChatMessage as AgentChatMessage,
  type EmbeddedQuestion,
  type AnswerValue,
} from '../../hooks/useAgentChat'
import {
  ChatInput,
  ChatAttachment,
  type ChatInputHandle,
  type ChatSendDisposition,
} from '../chat/ChatInput'
import { ANSWER_FIELD_SELECTOR, QuestionCard } from '../chat/QuestionCard'
import { StyleChoiceCard } from '../chat/StyleChoiceCard'
import { BatchedQuestionCard } from '../chat/BatchedQuestionCard'
import { MentionRenderProvider, ResolvedMentionChip, remarkMentionChips, resolveMentionCandidate, type MentionRenderContextValue } from '../chat/MentionMarkup'
import { ExecutionPlanCard } from '../chat/ExecutionPlanCard'
import { LibraryPickerCard } from '../chat/LibraryPickerCard'
import { CreateProjectCard } from '../chat/CreateProjectCard'
import { AnsweredConfigCard, ProjectSetupCard } from '../chat/ProjectSetupCard'
import { GlobalConfigActionCard } from '../chat/GlobalConfigActionCard'
import { VoiceCatalogueActionCard } from '../chat/VoiceCatalogueActionCard'
import { ghostTextByMode, WELCOME_SAMPLE_PROMPT_INDICES } from '../landing/hero-content'
import { authenticatedFetch, getConfiguredApiBase, type CanvasSummary } from '../../api/client'
import { ExecutionPlanStack } from '../chat/ExecutionPlanStack'
import { useNavigate } from 'react-router-dom'
import { stripInternalBlocks } from './messageContent'
import { useCreateProject, useProjectConfig } from '../../api/hooks'
import { useActiveCanvasId } from '../../contexts/ActiveCanvasIdContext'
import type { ChatEventCreateProjectMeta } from '../../hooks/useChatSocket'
import {
  DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  type AutoConfirmCostSettings,
} from '../chat/autoConfirmCost'
import { CollapsibleCodeBlock } from '../chat/CollapsibleCodeBlock'
import { DirectorThought } from '../ui/DirectorThought'
import { DirectorTurnActivity } from '../chat/DirectorTurnActivity'
import { buildCandidates, type MentionCandidate } from '../canvas-v2/nodes/shared/mentionUtils'
import { ReadOnlyMentionChip } from '../chat/MentionChip'
import {
  InlineMentionComposer,
  type InlineMentionComposerHandle,
  type InlineMentionComposerState,
} from '../chat/InlineMentionComposer'
import { ReplayOperationCards } from './ReplayOperationCards'
import type { DirectorSkillSummary } from '../../api/directorSkills'
import type { ClassifiedMediaFile } from '../../utils/uploadFileValidation'
import { trackChatCard, trackExecutionPlan, trackInteraction } from '../../analytics/interactionAnalytics'
import { noteChatReference } from '../../analytics/chatReferenceOrigin'

const log = createDebug('dw:chat:ui')
const questionTraceLog = createNadDebug('dw:chat:question-render')
const questionCardLog = createNadDebug('dw:chat:question-card')
const mentionChipRenderLog = createNadDebug('dw:chat:mention-chip')
const freeTextAnswerLog = createNadDebug('dw:director-free-text-answers:question-card')
const crossCanvasResponseLog = createNadDebug('dw:cross-canvas-response')
const scrollLog = createNadDebug('dw:chat-scroll')
const taskStatusLog = createNadDebug('dw:chat:task-status')
const noop = () => {}
/**
 * Ceiling on the cold-load Director hint. Comfortably longer than a normal
 * tool round, short enough that any stale-input path self-clears while the
 * user is still looking at the panel.
 */
const COLD_DIRECTOR_HINT_MAX_SILENCE_MS = 10 * 60 * 1000
const promptQueueLog = createNadDebug('dw:chat:prompt-queue')
const welcomeLog = createNadDebug('dw:welcome:question-card')
const USER_SCROLL_INTENT_WINDOW_MS = 1000
// `WELCOME_SAMPLE_PROMPT_INDICES` is imported from hero-content.ts, which owns
// both the index list and the English fallback array it indexes — see the
// rationale for index-based (not substring-based) selection there.

type ChatCanvasNode = ReactFlowCanvasNode
type ChatCanvasEdge = ReactFlowCanvasEdge
type ExecutionPlanNode = { node_id: string; type: string; action?: string; label: string; sparks: number }

interface GenerateScenesDetail {
  cards?: Array<{ sceneNumber: number; purpose: string; storyBeat: string }>
  tone?: string
  hook?: { text?: string }
}

interface CardActionDetail {
  action?: string
  card?: { sceneNumber?: number; purpose?: string; storyBeat?: string }
  context?: { tone?: string }
}

interface AgentChatSendDetail {
  message?: string
  referencedNodeIds?: string[]
}

export function mergeScopedReferences(
  referencedNodeIds: readonly string[] | undefined,
  forcedReferencedNodeIds: readonly string[],
): string[] {
  return Array.from(new Set([...(referencedNodeIds ?? []), ...forcedReferencedNodeIds]))
}

function isGenerateScenesEvent(event: Event): event is CustomEvent<GenerateScenesDetail> {
  return event instanceof CustomEvent
}

function isCardActionEvent(event: Event): event is CustomEvent<CardActionDetail> {
  return event instanceof CustomEvent
}

function isAgentChatSendEvent(event: Event): event is CustomEvent<AgentChatSendDetail> {
  return event instanceof CustomEvent
}

function replayScenarioStorageKey(projectId: string, canvasId: string): string {
  return `dreamwright_replay_scenario:${projectId}:${canvasId}`
}

function readStoredReplayScenario(projectId: string, canvasId: string): string | undefined {
  try {
    const stored = sessionStorage.getItem(replayScenarioStorageKey(projectId, canvasId))
    return stored || undefined
  } catch {
    return undefined
  }
}

function writeStoredReplayScenario(projectId: string, canvasId: string, replayScenario: string): void {
  try {
    sessionStorage.setItem(replayScenarioStorageKey(projectId, canvasId), replayScenario)
  } catch {
    // Best-effort developer replay state; the Director turn still runs without this cache.
  }
}

function parseExecutionPlanNodes(
  value: NonNullable<EmbeddedQuestion['metadata']>['nodes'],
): ExecutionPlanNode[] {
  return (value ?? []).map((node, index) => ({
    node_id: node.node_id ?? `node-${index}`,
    type: node.type ?? '',
    // Coerce '' / null → undefined so the card's action-label lookup never indexes
    // with an empty string (mirrors useExecutionPlan's live-path coercion).
    action: node.action || undefined,
    label: node.label ?? node.node_id ?? `Node ${index + 1}`,
    sparks: node.sparks ?? 0,
  }))
}

function getAnswerKey(question: EmbeddedQuestion): string {
  return question.tool_use_id
}


function isPageableClarificationQuestion(question: EmbeddedQuestion): boolean {
  if (isSchedulingQuestionId(getAnswerKey(question))) return false
  return question.metadata?.render_as !== 'library_picker'
    && question.metadata?.render_as !== 'create_project_button'
    && question.metadata?.render_as !== 'project_setup_card'
    && question.metadata?.render_as !== 'global_config_action_card'
    && question.metadata?.render_as !== 'voice_catalogue_action_card'
    && question.metadata?.render_as !== 'style_choice_card'
}

/**
 * The clarification questions that must all be answered before a clarification
 * answer is submitted. The cards from a single Director `ask_user_question` tool
 * call submit as one batch (RFC-1121 — submitting a subset would resume the tool
 * call and duplicate later answers), so real questions still gate together.
 *
   * Scheduling questions (`__cost_confirmation__`, `__insufficient_funds__`, and
   * other execution-plan action cards) are
 * a SEPARATE surface (ExecutionPlanCard / "Let's go!") and must never gate a
 * clarification submission. Otherwise, when a cost/plan card is co-pending,
 * typing a custom answer (or clicking an option) never reaches `allAnswered`, so
 * the answer is silently deferred and never POSTed — the user's input is
 * dropped and the cards later reset (Workplace post 1584335133323784).
 */
function clarificationGatingQuestions(
  questions: EmbeddedQuestion[],
): EmbeddedQuestion[] {
  return questions.filter((q) => !isSchedulingQuestionId(getAnswerKey(q)))
}

function selectedOptionsSummary(answers: Record<string, AnswerValue>): string {
  return Object.entries(answers)
    .map(([key, answer]) => `${key}:${answer.selected_option}`)
    .join(',')
}

function answerValueFromDraft(
  questionId: string,
  answerDrafts: Record<string, AnswerValue>,
  customTexts: Record<string, string>,
): AnswerValue | undefined {
  const custom = customTexts[questionId]?.trim()
  if (custom) {
    return { selected_option: 'user_feedback', custom_text: custom }
  }
  return answerDrafts[questionId]
}

function pluralTask(count: number, label: string): string {
  return `${count} task${count === 1 ? '' : 's'} ${label}`
}

function taskActivitySummary(
  runningTaskCount: number,
  queuedTaskCount: number,
): string {
  const parts: string[] = []
  if (runningTaskCount > 0) parts.push(`${runningTaskCount} running`)
  if (queuedTaskCount > 0) parts.push(`${queuedTaskCount} queued`)
  if (parts.length > 1) return parts.join(', ')
  if (runningTaskCount > 0) return pluralTask(runningTaskCount, 'running')
  if (queuedTaskCount > 0) return pluralTask(queuedTaskCount, 'queued')
  return ''
}

/**
 * Check if an embedded question is unanswered.
 * Valid answers are always non-empty strings.
 */
function isUnanswered(q: EmbeddedQuestion): boolean {
  return !q.answer
}

interface AnsweredQuestionPresentation {
  answer: string
  kind: 'preset' | 'custom'
  selectedOption?: string
}

function answeredQuestionPresentation(
  question: EmbeddedQuestion,
): AnsweredQuestionPresentation | null {
  if (!question.answer || question.answer === '__skipped__') return null

  const answer = question.answer.replace(/^User selected: /, '')
  if (question.selected_option === 'user_feedback') {
    const customText = question.custom_text?.trim()
    return { answer: customText || answer, kind: 'custom' }
  }
  if (question.selected_option) {
    return { answer, kind: 'preset', selectedOption: question.selected_option }
  }

  const matchesPreset = question.options.some(
    (option) => (option.value || option.label) === answer || option.label === answer,
  )
  return {
    answer,
    kind: question.allow_custom_input && !matchesPreset ? 'custom' : 'preset',
    selectedOption: answer,
  }
}

function hasCompleteAnsweredQuestionPresentation(question: EmbeddedQuestion): boolean {
  return question.answer === '__skipped__'
    || answeredQuestionPresentation(question) !== null
}

function isSkippedTranscriptQuestion(question: EmbeddedQuestion): boolean {
  return question.question_type === 'director'
    && question.answer === '__skipped__'
    && isPageableClarificationQuestion(question)
}

function rendersQuestionTranscriptCard(question: EmbeddedQuestion): boolean {
  return answeredQuestionPresentation(question) !== null
    || isSkippedTranscriptQuestion(question)
}

/**
 * Get embedded questions that need user response from a message.
 */
function getUnansweredQuestions(msg: AgentChatMessage): EmbeddedQuestion[] {
  if (!msg.questions) return []
  return msg.questions.filter(isUnanswered)
}

// Custom components for react-markdown styling
const markdownComponents = {
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc pl-4 my-1.5 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal pl-4 my-1.5 space-y-0.5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-text-secondary">{children}</li>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-1.5">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-medium text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="text-text-secondary">{children}</em>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isInline = !className?.includes('language-')
    if (isInline) {
      return (
        <code className="bg-surface-1 px-1 py-0.5 rounded text-xs font-medium font-mono text-text-primary">{children}</code>
      )
    }
    return <>{children}</>
  },
  pre: ({ children }: { children?: React.ReactNode }) => {
    const className = isValidElement<{ className?: string }>(children)
      ? children.props.className || ''
      : ''
    const match = /language-(\w+)/.exec(className)
    return (
      <CollapsibleCodeBlock language={match?.[1]}>
        {children}
      </CollapsibleCodeBlock>
    )
  },
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-base font-medium text-text-primary mt-2 mb-1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-sm font-medium text-text-primary mt-2 mb-1">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-sm font-medium text-text-primary mt-1.5 mb-0.5">{children}</h3>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} className="text-text-secondary underline underline-offset-2 hover:text-text-primary" target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l border-border pl-2 my-1.5 text-text-muted">{children}</blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-xs font-medium">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-panel">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-border-subtle">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="border-b border-border-subtle">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left font-semibold text-text-secondary border border-border">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 font-normal text-text-secondary border border-border-subtle">{children}</td>
  ),
  // `remarkMentionChips` emits `<span data-mention-node-id>` for each `@[[id]]`.
  span: ({ children, ...props }: { children?: React.ReactNode; 'data-mention-node-id'?: string }) => {
    const nodeId = props['data-mention-node-id']
    if (nodeId) return <ResolvedMentionChip nodeId={nodeId} surface="director_markdown" />
    return <span>{children}</span>
  },
}

/**
 * Hoisted so the array identity is stable. Cosmetic on its own — see below —
 * but it keeps the prop out of the render path's allocation churn.
 */
const REMARK_PLUGINS = [remarkGfm, remarkMentionChips]

/**
 * Markdown that only re-parses when its TEXT changes.
 *
 * `react-markdown` v10 does no caching whatsoever:
 *
 *     export function Markdown(options) {
 *       const processor = createProcessor(options)
 *       return post(processor.runSync(processor.parse(file), file), options)
 *     }
 *
 * `parse()` runs synchronously on every render, so prop identity buys nothing
 * and the ONLY way to avoid the work is to not re-render the subtree. Hence a
 * `memo` boundary whose sole prop is a string.
 *
 * Why this rather than only memoizing `MessageBubble`: the bubble takes eight
 * props including per-message callbacks, so its memo is one careless inline
 * arrow away from silently reverting. This boundary has a single primitive prop
 * and cannot be defeated from the outside, so the expensive work stays cached
 * even when the bubble around it legitimately re-renders (status flips, a new
 * message arrives, an edit opens).
 *
 * Measured cost of NOT having this: a canvas-pan profile attributed 4652ms —
 * 64% of all non-idle CPU — to micromark/remark re-tokenizing chat history,
 * against 66ms for ReactFlow itself. Panning felt broken because every
 * `AgentChat` render re-parsed every Director message.
 */
export const MemoizedMarkdown = memo(function MemoizedMarkdown({ content }: { content: string }) {
  return (
    <Markdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
      {content}
    </Markdown>
  )
})

/** A prompt queued while the agent is still running */
interface QueuedPrompt {
  id: string
  editRevision: number
  message: string
  attachments?: ChatAttachment[]
  referencedNodeIds?: string[]
  selectedSkill?: DirectorSkillSummary
  selectedSkillPosition?: number
}

export interface DirectorControl {
  active: boolean
  stop: () => Promise<void>
}

interface AgentChatProps {
  onDirectorControlChange?: (control: DirectorControl) => void
  projectId: string
  canvasId?: string
  initialPrompt?: string
  initialSelectedSkill?: DirectorSkillSelection
  initialReferencedNodeIds?: string[]
  /** Node references appended to every prompt sent from a scoped chat surface. */
  forcedReferencedNodeIds?: string[]
  /** Focused desks can replace new-project suggestions with task-specific help. */
  scopedWelcome?: string
  /** Deterministic tool actions to execute before the Director's first turn.
   *  Used by the sequel flow: imports run via the Director tools REST endpoint
   *  (POST /projects/{id}/agentic/director/tools/{tool}, guaranteed, no LLM)
   *  before startWithPrompt fires. */
  pendingActions?: Array<{ tool: string; args: Record<string, string> }>
  initialReplayScenario?: string
  className?: string
  /** Callback when execution is stopped */
  onStop?: () => void
  /** Auto-confirm cost estimation prompts without user interaction */
  autoConfirmCostSettings?: AutoConfirmCostSettings
  /** Canvas nodes for @mention autocomplete in the chat input */
  canvasNodes?: ChatCanvasNode[]
  /** Live canvas nodes for replay operation checks that validate output fields */
  replayCanvasNodes?: ChatCanvasNode[]
  /** Canvas edges for replay operation integrity checks */
  canvasEdges?: ChatCanvasEdge[]
  /** Replay-only canvas create operation used by in-app operation cards */
  onReplayCreateCanvas?: (title: string) => Promise<{ id: string; title: string }>
  /** Replay-only canvas delete operation used by in-app operation cards */
  onReplayDeleteCanvas?: (canvasId: string) => Promise<void>
  /** Live Director-created canvas event from the chat websocket */
  onCanvasCreated?: (canvas: CanvasSummary) => void
  /**
   * Fires on every chat WebSocket connect/disconnect transition. Callers
   * reconcile REST-only server state (e.g. the canvas tab list) on
   * `connected===true` — the `canvas_created` broadcast is not queued for late
   * subscribers, so any events fired before the WS handshake finished (a
   * common race on modal "Ask Director" → 100 ms → send) are only recovered
   * by a REST refetch after the socket comes up.
   */
  onChatSocketConnectedChange?: (connected: boolean) => void
  /** Running non-director node count from YDoc (includes canvas-hidden nodes) */
  ydocRunningCount?: number
  /** Queued (pending_execution) non-director node count from YDoc */
  ydocQueuedCount?: number
  /** True when the director node's own YDoc status is `running` */
  ydocDirectorRunning?: boolean
  /**
   * True when `ydocDirectorRunning` reflects a live, synced document rather
   * than a pre-sync default or a post-disconnect memory.
   */
  ydocStatusLive?: boolean
  /** Called when a mention pill is clicked — focuses the canvas on that node */
  onMentionClick?: (nodeId: string) => void
  /** Called with a merged "Upload media" selection from Director chat */
  onUploadMedia?: (accepted: ClassifiedMediaFile[], rejected: File[]) => void
  /** Execution plan batches from YDoc */
  executionPlanBatches?: import('../../hooks/useExecutionPlan').ExecutionPlanBatch[]
  /** Confirm an execution plan batch */
  onConfirmBatch?: (quotes: ApprovalRef[]) => void | Promise<void>
  /** Cancel an execution plan batch */
  onCancelBatch?: (batchId: string) => void
}

export const AgentChat = memo(function AgentChat({
  projectId,
  canvasId = 'agent',
  initialPrompt,
  initialSelectedSkill,
  initialReferencedNodeIds,
  forcedReferencedNodeIds = [],
  scopedWelcome,
  pendingActions,
  initialReplayScenario,
  className = '',
  onStop,
  onDirectorControlChange,
  autoConfirmCostSettings = DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  canvasNodes,
  replayCanvasNodes,
  canvasEdges,
  onReplayCreateCanvas,
  onReplayDeleteCanvas,
  onCanvasCreated,
  onChatSocketConnectedChange,
  ydocRunningCount = 0,
  ydocQueuedCount = 0,
  ydocDirectorRunning = false,
  ydocStatusLive = false,
  onMentionClick,
  onUploadMedia,
  executionPlanBatches = [],
  onConfirmBatch,
  onCancelBatch,
}: AgentChatProps) {
  const messageMentionCandidates = useMemo(
    () => buildCandidates(canvasNodes ?? []),
    [canvasNodes],
  )
  // Axis 3: read the persisted per-project content/output language and hand it
  // to useAgentChat so it is sent on /agent/connect — independent of the UI
  // locale (axis 1) and the Director conversation language (axis 2). Track the
  // query's loading state so the auto-start effect waits for it to settle before
  // firing the first /agent/connect (otherwise a persisted content_language
  // could be missed on the initial prompt).
  const activeCanvasId = useActiveCanvasId()
  const { data: projectConfig, isLoading: isProjectConfigLoading } = useProjectConfig(
    projectId,
    activeCanvasId,
  )
  // "The turn this client last watched the director node run has ended." Set
  // from the YDoc node status further down, where the reasoning lives; declared
  // here because the three turn-starting calls below have to clear it.
  const [directorTurnEnded, setDirectorTurnEnded] = useState(false)
  const sawDirectorNodeRunningRef = useRef(false)
  const {
    messages,
    firstUnansweredMessageIndex,
    status,
    error,
    isLoading,
    startWithPrompt: startWithPromptRaw,
    respondToQuestions: respondToQuestionsRaw,
    dismissPendingQuestions,
    deleteMessage,
    editMessage: editMessageRaw,
    stop,
    reconcileDirectorTurnEnd,
    awaitingDirector,
    chatSilentMs,
    activeDirectorToolCount,
    chatSocketConnected,
    chatConnectionState,
  } = useAgentChat(projectId, canvasId, {
    contentLanguage: projectConfig?.content_language ?? null,
    autoConfirmCostSettings,
    onCanvasCreated,
  })

  // Dispatching a new turn invalidates the "that turn ended" verdict, and it
  // has to happen here rather than on an `awaitingDirector` edge: in the very
  // state this guards against, `awaitingDirector` is already stuck true, so
  // there is no edge to observe. Without the reset the veto would still be
  // holding `shouldQueuePrompt` false when the *next* prompt arrives, and a
  // message that belongs in the FIFO queue would be sent straight through.
  // `sawDirectorNodeRunningRef` is already false, so the veto only re-arms
  // once this client has seen the node run and finish again.
  const startWithPrompt = useCallback(
    (...args: Parameters<typeof startWithPromptRaw>) => {
      setDirectorTurnEnded(false)
      return startWithPromptRaw(...args)
    },
    [startWithPromptRaw],
  )
  const respondToQuestions = useCallback(
    (...args: Parameters<typeof respondToQuestionsRaw>) => {
      setDirectorTurnEnded(false)
      return respondToQuestionsRaw(...args)
    },
    [respondToQuestionsRaw],
  )
  const editMessage = useCallback(
    (...args: Parameters<typeof editMessageRaw>) => {
      setDirectorTurnEnded(false)
      return editMessageRaw(...args)
    },
    [editMessageRaw],
  )

  // Notify parent on every chat WS connect/disconnect transition. Used by
  // WorkspacePageV2 to refetch the REST canvas list when the socket comes up
  // — the `canvas_created` broadcast is not queued for late subscribers, so
  // events fired while AgentChat was unmounted or the WS was still handshaking
  // are only recovered by a REST refetch on connect.
  useEffect(() => {
    onChatSocketConnectedChange?.(chatSocketConnected)
  }, [chatSocketConnected, onChatSocketConnectedChange])

  const { t, i18n } = useTranslation('workspace')
  const mentionRenderValue = useMemo<MentionRenderContextValue>(
    () => ({
      candidates: messageMentionCandidates,
      projectId,
      onMentionClick,
      focusLabel: t('agentChat.focusOnCanvas'),
      removedLabel: t('agentChat.removedNodeChip'),
    }),
    [messageMentionCandidates, projectId, onMentionClick, t],
  )

  // The welcome options live in the `landing` namespace because the landing
  // hero owns the same list. `ghostTextByMode.video` remains the English
  // fallback (hero-content.ts designates it as such) for the case where the
  // locale bundle is missing or malformed.
  const welcomeSamplePrompts = useMemo(() => {
    const localized = t('landing:hero.ghostTextIdeas.video', { returnObjects: true })
    const translated = Array.isArray(localized)
      ? localized.filter((idea): idea is string => typeof idea === 'string')
      : []
    const usedFallback = translated.length !== ghostTextByMode.video.length
    const ideas = usedFallback ? ghostTextByMode.video : translated
    welcomeLog(
      'welcome_options_resolved locale=%s used_fallback=%s source_count=%d',
      i18n.language,
      usedFallback,
      ideas.length,
    )
    return WELCOME_SAMPLE_PROMPT_INDICES
      .map((index) => ideas[index])
      .filter((prompt): prompt is string => typeof prompt === 'string')
  }, [t, i18n.language])

  const chatUiScopeKey = `${projectId}:${canvasId || 'agent'}`
  const sequelNavigate = useNavigate()
  const createProject = useCreateProject()

  // Combined stop handler that calls hook's stop and parent callback
  const stopDirector = useCallback(async () => {
    await stop()
    sawDirectorNodeRunningRef.current = false
    setDirectorTurnEnded(true)
    onStop?.()
  }, [stop, onStop])
  const handleStop = useCallback(() => {
    // Only reached from the composer's Stop button (ChatInput onStop).
    trackInteraction('chat_turn_cancelled', { project_id: projectId })
    return stopDirector().catch((cause) => {
      toast.error(cause instanceof Error ? cause.message : String(cause))
    })
  }, [stopDirector, projectId])

  const runningTaskCount = ydocRunningCount + activeDirectorToolCount
  const queuedTaskCount = ydocQueuedCount
  const hasTaskActivity = runningTaskCount > 0 || queuedTaskCount > 0
  const taskSummary = useMemo(
    () => taskActivitySummary(runningTaskCount, queuedTaskCount),
    [queuedTaskCount, runningTaskCount],
  )
  const previousTaskCountsRef = useRef({
    running: 0,
    queued: 0,
    visible: false,
  })
  useEffect(() => {
    const previous = previousTaskCountsRef.current
    if (
      previous.running === runningTaskCount &&
      previous.queued === queuedTaskCount &&
      previous.visible === hasTaskActivity
    ) {
      return
    }
    log(
      'runningTasks running=%d queued=%d visible=%s',
      runningTaskCount,
      queuedTaskCount,
      hasTaskActivity,
    )
    taskStatusLog({
      event: 'chat-task-card-visibility',
      trace_id: `chat-task-status:${projectId}`,
      projectId,
      runningCount: runningTaskCount,
      queuedCount: queuedTaskCount,
      visible: hasTaskActivity,
      summary: taskSummary,
    })
    previousTaskCountsRef.current = {
      running: runningTaskCount,
      queued: queuedTaskCount,
      visible: hasTaskActivity,
    }
  }, [
    hasTaskActivity,
    projectId,
    queuedTaskCount,
    runningTaskCount,
    taskSummary,
  ])

  // Compute display messages: show up to and including first message with unanswered questions
  // This implements sequential display - user sees messages one-by-one, pausing at questions
  // Uses firstUnansweredMessageIndex from backend instead of computing locally
  const { displayMessages, currentQuestions } = useMemo(() => {
    const slice = firstUnansweredMessageIndex === -1
      ? messages
      : messages.slice(0, firstUnansweredMessageIndex + 1)

    // Hide noise from auto-confirm flows:
    // - Assistant messages whose only questions are answered cost_confirmations
    //   AND have no textual content worth showing
    // - User "continue"/"confirm" bubbles that immediately follow such messages
    const isAutoAcceptedAssistant = (msg: AgentChatMessage) => {
      if (msg.role !== 'assistant' || !msg.questions?.length) return false
      return msg.questions.every((q) => {
        if (q.answer == null) return false
        if (q.tool_use_id?.startsWith('__cost_confirmation__')) return true
        return false
      })
    }

    // Check if a message is an assistant with answered director questions
    // (answers now shown inline on the card, so the echo user bubble is redundant)
    const hasAnsweredDirectorQuestions = (msg: AgentChatMessage) => {
      if (msg.role !== 'assistant' || !msg.questions?.length) return false
      return msg.questions.some(
        (q) => q.question_type === 'director' && q.answer && q.answer !== '__skipped__',
      )
    }

    // A Director (assistant) MessageBubble renders visible cards inline for
    // answered questions and skipped real Director questions. Mirror that
    // exactly so the filter's notion of "renders something" matches the
    // bubble's.
    const assistantHasInlineCard = (msg: AgentChatMessage) => {
      const pageableQuestions = (msg.questions ?? []).filter(isPageableClarificationQuestion)
      const hasCompletedPageableBatch = pageableQuestions.length > 1
        && pageableQuestions.every(hasCompleteAnsweredQuestionPresentation)
      const hasTranscriptQuestionCard = !!msg.questions?.some(rendersQuestionTranscriptCard)
      if (hasTranscriptQuestionCard && msg.questions?.some(isSkippedTranscriptQuestion)) {
        questionTraceLog({
          event: 'skipped-question-transcript-card',
          messageId: msg.id,
          questionCount: msg.questions.length,
        })
      }
      return hasCompletedPageableBatch || hasTranscriptQuestionCard
    }

    // An assistant message that has no body text (after stripping internal
    // dag-proposal/story-* blocks, exactly as MessageBubble does) AND no inline
    // card would render as a blank bubble with only a trash button. Suppress it
    // so the chat stays well-formed. The live pending execution-plan card is
    // rendered separately by ExecutionPlanStack (YDoc-sourced) and the legacy
    // inline cost card is collected from `slice` below, so neither is hidden.
    const assistantRendersNothing = (msg: AgentChatMessage) => {
      if (msg.metadata || msg.actionCardError) return false
      if (msg.role !== 'assistant') return false
      if (msg.presentation?.mode === 'summary') return false
      if (cleanAiText(stripInternalBlocks(msg.content ?? '')).trim()) return false
      return !assistantHasInlineCard(msg)
    }

    const filtered = slice.filter((msg, i, arr) => {
      // Suppress assistant messages that would render an empty bubble (no body
      // text after stripping internal blocks, and no inline card to show).
      if (assistantRendersNothing(msg)) {
        questionTraceLog({
          event: 'chat-empty-bubble:suppressed',
          messageId: msg.id,
          hasQuestions: !!msg.questions?.length,
          rawContentLength: msg.content?.length ?? 0,
        })
        return false
      }
      if (msg.role === 'user') {
        const prev = arr[i - 1]
        // Hide "continue"/"confirm" echoes after auto-accepted messages
        if ((msg.content === 'continue' || msg.content === 'confirm') && prev && isAutoAcceptedAssistant(prev) && !prev.content?.trim()) {
          return false
        }
        // Hide user answer echo after assistant message with answered questions
        // (the answer is shown inline on the QuestionCard/ExecutionPlanCard)
        if (prev && hasAnsweredDirectorQuestions(prev)) {
          const answeredTexts = prev.questions!
            .filter((q) => q.question_type === 'director' && q.answer && q.answer !== '__skipped__')
            .map((q) => q.answer!.replace(/^User selected: /, ''))
          const userContent = msg.content?.trim()
          if (userContent) {
            // Backend formats as "answer1; answer2" — check both individual and joined forms
            if (
              answeredTexts.some((a) => userContent === a || userContent.includes(a) || a.includes(userContent)) ||
              userContent === answeredTexts.join('; ')
            ) {
              return false
            }
          }
        }
      }
      return true
    })

    const currentQuestions: EmbeddedQuestion[] = firstUnansweredMessageIndex === -1
      ? []
      : getUnansweredQuestions(messages[firstUnansweredMessageIndex])

    // Scheduling questions are non-gating (firstUnansweredMessageIndex
    // skips them) so they won't appear in currentQuestions above. Collect them
    // from `slice` (the pre-suppression display window) rather than `filtered`
    // so suppressing an empty bubble can never starve the inline scheduling card.
    const schedulingQuestions: EmbeddedQuestion[] = []
    for (const msg of slice) {
      if (!msg.questions) continue
      for (const q of msg.questions) {
        if (!q.answer && q.tool_use_id && isSchedulingQuestionId(q.tool_use_id)) {
          schedulingQuestions.push(q)
        }
      }
    }

    questionTraceLog({
      event: 'display-messages-computed',
      totalMessages: messages.length,
      firstUnansweredIdx: firstUnansweredMessageIndex,
      sliceLength: filtered.length,
      currentQuestionCount: currentQuestions.length,
      schedulingQuestionCount: schedulingQuestions.length,
      currentToolIds: currentQuestions.map(q => q.tool_use_id).join(','),
    })

    return {
      displayMessages: filtered,
      currentQuestions: [
        ...currentQuestions.filter(
          (q) => !q.tool_use_id || !isSchedulingQuestionId(q.tool_use_id),
        ),
        ...schedulingQuestions,
      ],
    }
  }, [messages, firstUnansweredMessageIndex])

  // Questions from embedded messages (no more legacy pending questions)
  const effectiveQuestions: EmbeddedQuestion[] = currentQuestions
  const effectiveQuestionIds = effectiveQuestions.map(getAnswerKey).join('\u0000')
  const visibleQuestions = useMemo(
    () => effectiveQuestions,
    [effectiveQuestions],
  )
  const pageableQuestions = useMemo(() => {
    const gatingQuestions = clarificationGatingQuestions(effectiveQuestions)
    const pageable = gatingQuestions.filter(isPageableClarificationQuestion)
    return pageable.length === gatingQuestions.length ? pageable : []
  }, [effectiveQuestions])
  // System prompts start with __ and do not block normal prompt routing.
  const hasRealQuestions = effectiveQuestions.length > 0 &&
    effectiveQuestions.some(q => !getAnswerKey(q).startsWith('__'))
  const loggedCrossCanvasConfirmationIdsRef = useRef(new Set<string>())

  useEffect(() => {
    for (const question of visibleQuestions) {
      if (question.metadata?.render_as !== 'cross_canvas_response_confirmation') continue
      if (!question.tool_use_id) continue
      const confirmationScopeId = `${projectId}:${canvasId}:${question.tool_use_id}`
      if (loggedCrossCanvasConfirmationIdsRef.current.has(confirmationScopeId)) continue
      loggedCrossCanvasConfirmationIdsRef.current.add(confirmationScopeId)
      crossCanvasResponseLog({
        event: 'cross-canvas-response:confirmation_card_rendered',
        trace_id: `cross-canvas-response:${projectId}:${canvasId}:${question.tool_use_id}`,
        project_id: projectId,
        source_canvas_id: canvasId,
        target_canvas_id: question.metadata.target_canvas_id,
        director_id: question.metadata.director_id,
        round_number: question.metadata.round_number,
        confirmation_card_id: question.tool_use_id,
      })
    }
  }, [canvasId, projectId, visibleQuestions])

  // NAD: record the identity-chip render/skip decision once per plain director
  // card (feature tag: director-card-node-ref). Cost/library/create-project
  // cards take separate render branches and never carry node_ref.
  const loggedIdentityChipIdsRef = useRef(new Set<string>())

  useEffect(() => {
    for (const question of visibleQuestions) {
      if (!question.tool_use_id) continue
      if (isSchedulingQuestionId(question.tool_use_id)) continue
      if (question.metadata?.render_as) continue
      const chipScopeId = `${projectId}:${canvasId}:${question.tool_use_id}`
      if (loggedIdentityChipIdsRef.current.has(chipScopeId)) continue
      loggedIdentityChipIdsRef.current.add(chipScopeId)
      const nodeRef = question.metadata?.node_ref
      questionCardLog({
        event: 'question-card:identity_chip',
        tool_use_id: question.tool_use_id,
        node_id: nodeRef?.node_id ?? null, // join key back to the backend card_node_ref event
        kind: nodeRef?.kind ?? null,
        hasNumber: typeof nodeRef?.number === 'number',
        hasVersion: typeof nodeRef?.version === 'number',
        rendered: !!nodeRef,
      })
    }
  }, [canvasId, projectId, visibleQuestions])

  const [queuedPrompts, setQueuedPrompts] = useState<QueuedPrompt[]>([])
  const queuedPromptsRef = useRef<QueuedPrompt[]>([])
  const activeChatUiScopeKeyRef = useRef(chatUiScopeKey)
  const queuedPromptsByScopeRef = useRef(new Map<string, QueuedPrompt[]>())
  const queuedPromptSequenceRef = useRef(0)
  const queueActivityCycleSequenceRef = useRef(0)
  const retrievedPromptForEditRef = useRef<QueuedPrompt | null>(null)

  const replaceQueuedPrompts = useCallback((next: QueuedPrompt[]) => {
    queuedPromptsRef.current = next
    queuedPromptsByScopeRef.current.set(activeChatUiScopeKeyRef.current, next)
    setQueuedPrompts(next)
  }, [])

  useEffect(() => {
    const previousScopeKey = activeChatUiScopeKeyRef.current
    if (previousScopeKey === chatUiScopeKey) return
    queuedPromptsByScopeRef.current.set(previousScopeKey, queuedPromptsRef.current)
    const next = queuedPromptsByScopeRef.current.get(chatUiScopeKey) ?? []
    queuedPromptsRef.current = next
    setQueuedPrompts(next)
    activeChatUiScopeKeyRef.current = chatUiScopeKey
    retrievedPromptForEditRef.current = null
  }, [chatUiScopeKey])

  // RFC-0289: Track whether the Director is actively thinking/streaming.
  // Uses `awaitingDirector` from the hook (set on send, cleared on first
  // stream chunk / done / error) instead of local state, which avoids
  // flicker when SSE events arrive with brief gaps.
  //
  // Director tool execution is counted in the task card. The DirectorThought
  // indicator is only for active text streaming / waiting for a text response.
  // Both live signals above are LOCAL guesses that only ever clear on a chat
  // event: `awaitingDirector` on `chat_stream_end`/`done`/`error`, a `stream-`
  // bubble on the `chat_stream_end` carrying its own message id. When the
  // terminal event for a turn is never applied by this client they stay set,
  // and the 45s reconciler in `useAgentChat` cannot help: it clears only when
  // the backend reports a status other than `running`, and the backend's
  // status is the ORCHESTRATOR's, which stays `running` for as long as any
  // worker job is in flight — Director turn or not.
  //
  // Workplace post 1622077266216237, prod-meta `untitled-project-3e7e174d`
  // 2026-09-17: the Director finished at 06:58:13.197 and YDoc mirrored
  // `director-1789626712081 -> completed` 44ms later, but six fal Seedance
  // clips the same turn had just fired kept the orchestrator at `running`
  // until every one of them hit a 600s ReadTimeout. The sidebar claimed
  // "Dreaming Now" for those 10 minutes, with the composer stuck showing a
  // stop button, and only a reload got out of it.
  //
  // The director node's own YDoc status is the server's verdict on whether a
  // turn is in flight, and it was correct the whole time. Once this client has
  // SEEN that node run and then leave `running`, stop trusting the local flags
  // for that turn.
  //
  // Requiring the observed running -> not-running TRANSITION, rather than just
  // "not running right now", is what keeps the send path flicker-free: between
  // the user's click (which sets `awaitingDirector` immediately) and the
  // backend mirroring `running` into YDoc, no transition has been seen yet, so
  // the veto cannot fire. A later wake-up that puts the node back to `running`
  // re-arms it for the next turn.
  // (`directorTurnEnded` / `sawDirectorNodeRunningRef` are declared next to the
  // `useAgentChat` call above, which wraps the turn-starting functions to clear
  // this verdict when a new turn is dispatched.)
  useEffect(() => {
    // A stale `ydocDirectorRunning` behind a dropped transport is a memory, not
    // an observation — the same reason the cold-load hint below gates on it.
    if (!ydocStatusLive) return
    if (ydocDirectorRunning) {
      sawDirectorNodeRunningRef.current = true
      setDirectorTurnEnded(false)
      return
    }
    if (sawDirectorNodeRunningRef.current) {
      sawDirectorNodeRunningRef.current = false
      setDirectorTurnEnded(true)
      reconcileDirectorTurnEnd()
    }
  }, [ydocStatusLive, ydocDirectorRunning, reconcileDirectorTurnEnd])

  const directorActivityReason = useMemo(() => {
    const localSignal = displayMessages.some(m => m.isStreaming)
      ? 'streaming'
      : awaitingDirector
        ? 'awaiting_director'
        : null
    if (localSignal && !directorTurnEnded) return localSignal
    // Server says the turn is over. Fall through to whatever is genuinely
    // still happening, so a batch the Director fired on its way out is
    // reported as worker progress instead of as the Director still thinking.
    if (hasTaskActivity) return 'worker_tasks_visible'
    if (localSignal) return 'director_turn_ended'
    return 'not_running'
  }, [
    displayMessages,
    awaitingDirector,
    hasTaskActivity,
    directorTurnEnded,
  ])
  const isDirectorActive = directorActivityReason !== 'not_running' &&
    directorActivityReason !== 'worker_tasks_visible' &&
    directorActivityReason !== 'director_turn_ended'
  const shouldQueuePrompt = isDirectorActive && !hasRealQuestions

  // Cold-load hint. `awaitingDirector` is only ever set by a local user action
  // (send / answer / edit-resend), so a hard refresh mid-turn leaves the panel
  // looking dead: no local memory, no streamed bubble yet, and the finalized
  // reply only lands when the whole turn ends. This reconstructs "the Director
  // is working" from state the client can observe on a cold mount.
  //
  // It is DISPLAY ONLY. It must never reach `isDirectorActive`, because that
  // gates prompt queueing (`shouldQueuePrompt` above) and queued prompts live
  // only in component state — a wrong signal there would swallow a message.
  //
  // Each conjunct defends a specific failure:
  //  - `status`         : the server's own verdict. A question pause reports
  //                       `pending_questions` and a crashed turn reports an
  //                       error, so both stale-YDoc false positives die here.
  //  - question guards  : belt-and-braces for the same pause, including
  //                       scheduling-only questions excluded from
  //                       `hasRealQuestions`.
  //  - positive evidence: the director node is LIVE and running. Two weaker
  //                       proxies were tried and rejected in review, both of
  //                       which mislabelled an unrelated worker run as Director
  //                       activity: "no workers are running" (the worker count
  //                       can read zero early in a user's own Generate click)
  //                       and "the transcript ends on a user turn" (a cancelled
  //                       turn leaves it that way forever, since the
  //                       cancellation bubble is client-only).
  //  - silence ceiling  : the inputs can go stale in ways no event corrects
  //                       (e.g. a leaked task registry entry keeps the reported
  //                       status at `running`). Cap the claim rather than
  //                       trust it forever.
  const coldDirectorHint =
    status === 'running' &&
    !hasRealQuestions &&
    visibleQuestions.length === 0 &&
    ydocStatusLive &&
    ydocDirectorRunning &&
    chatSilentMs < COLD_DIRECTOR_HINT_MAX_SILENCE_MS
  const showDirectorIndicator = isDirectorActive || coldDirectorHint
  // Shown whenever there is Director work the user is waiting on — including
  // a cold load where status is `running` but the indicator is hidden. An
  // idle chat has nothing to lose from a drop, so it stays quiet.
  const showReconnectingIndicator =
    chatConnectionState === 'reconnecting' &&
    (status === 'running' || awaitingDirector || showDirectorIndicator || hasTaskActivity)
  useEffect(() => {
    onDirectorControlChange?.({ active: showDirectorIndicator, stop: stopDirector })
  }, [onDirectorControlChange, showDirectorIndicator, stopDirector])
  const previousDirectorActivityRef = useRef({
    visible: false,
    reason: 'not_running',
  })
  useEffect(() => {
    // Tracks what the user actually SEES, so the cold-load hint shows up in
    // traces as its own reason rather than as an unexplained gap.
    const effectiveReason = isDirectorActive
      ? directorActivityReason
      : coldDirectorHint
        ? 'cold_snapshot_hint'
        : directorActivityReason
    const previous = previousDirectorActivityRef.current
    if (
      previous.visible === showDirectorIndicator &&
      previous.reason === effectiveReason
    ) {
      return
    }
    taskStatusLog({
      event: 'director-thinking-visibility',
      trace_id: `director-thinking:${projectId}`,
      projectId,
      visible: showDirectorIndicator,
      reason: effectiveReason,
      chatStatus: status,
      runningCount: runningTaskCount,
      queuedCount: queuedTaskCount,
      ydocStatusLive,
      ydocDirectorRunning,
      directorTurnEnded,
      chatSilentMs,
    })
    previousDirectorActivityRef.current = {
      visible: showDirectorIndicator,
      reason: effectiveReason,
    }
  }, [
    chatSilentMs,
    coldDirectorHint,
    directorActivityReason,
    directorTurnEnded,
    isDirectorActive,
    projectId,
    queuedTaskCount,
    runningTaskCount,
    showDirectorIndicator,
    status,
    ydocDirectorRunning,
    ydocStatusLive,
  ])
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, string>>({})
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, AnswerValue>>({})
  const [customTexts, setCustomTexts] = useState<Record<string, string>>({})  // RFC 0053
  const [skippedQuestionIds, setSkippedQuestionIds] = useState<Set<string>>(new Set())
  const [welcomeCustomText, setWelcomeCustomText] = useState('')
  const [dismissedError, setDismissedError] = useState(false)
  const [submittingQuestionIds, setSubmittingQuestionIds] = useState<Set<string>>(new Set())
  const clearSubmittingQuestions = useCallback((questionIds: Iterable<string>) => {
    const ids = new Set(questionIds)
    if (ids.size === 0) return
    setSubmittingQuestionIds((previous) => {
      let changed = false
      const next = new Set(previous)
      ids.forEach((id) => {
        changed = next.delete(id) || changed
      })
      return changed ? next : previous
    })
  }, [])
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false)
  const [activeReplayScenario, setActiveReplayScenario] = useState<string | undefined>(
    () => initialReplayScenario ?? readStoredReplayScenario(projectId, canvasId),
  )
  const isUserScrolledUpRef = useRef(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollContentRef = useRef<HTMLDivElement>(null)
  const questionsStartRef = useRef<HTMLDivElement>(null)
  const questionRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const hasStartedRef = useRef(false)
  const initialScrollDoneRef = useRef(false)
  const prevMessageCountRef = useRef(0)
  const prevDisplayMessageCountRef = useRef(0)
  const prevFirstUnansweredMessageIndexRef = useRef(firstUnansweredMessageIndex)
  const previousQueueDirectorActivityRef = useRef({
    scopeKey: chatUiScopeKey,
    active: isDirectorActive,
  })
  const scrollThrottleRef = useRef(0)
  const prevScrollTopRef = useRef(0)
  const lastUserScrollIntentAtRef = useRef(0)
  const lastUserScrollAwayIntentAtRef = useRef(0)
  const lastTouchYRef = useRef<number | null>(null)
  const lastPointerYRef = useRef<number | null>(null)

  useEffect(() => {
    hasStartedRef.current = false
    initialScrollDoneRef.current = false
    isUserScrolledUpRef.current = false
    prevScrollTopRef.current = 0
    lastUserScrollIntentAtRef.current = 0
    lastUserScrollAwayIntentAtRef.current = 0
    lastTouchYRef.current = null
    lastPointerYRef.current = null
    setIsUserScrolledUp(false)
    setWelcomeCustomText('')
  }, [chatUiScopeKey])

  useEffect(() => {
    if (initialReplayScenario) {
      setActiveReplayScenario(initialReplayScenario)
      writeStoredReplayScenario(projectId, canvasId, initialReplayScenario)
    }
  }, [canvasId, initialReplayScenario, projectId])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }, [])

  const handleReplayCardsUpdated = useCallback(() => {
    if (isUserScrolledUpRef.current) return
    requestAnimationFrame(() => scrollToBottom())
  }, [scrollToBottom])

  const markUserScrollIntent = useCallback((source: string, scrollAway = false) => {
    lastUserScrollIntentAtRef.current = Date.now()
    if (scrollAway) {
      lastUserScrollAwayIntentAtRef.current = lastUserScrollIntentAtRef.current
    }
    scrollLog({
      event: 'user-scroll-intent',
      trace_id: `director-chat-scroll:${projectId}`,
      projectId,
      project_id: projectId,
      source,
      scrollAway,
    })
  }, [projectId])

  const hasRecentUserScrollAwayIntent = useCallback(() => (
    Date.now() - lastUserScrollAwayIntentAtRef.current <= USER_SCROLL_INTENT_WINDOW_MS
  ), [])

  const hasPendingUserScrollAway = useCallback((el: HTMLElement) => (
    hasRecentUserScrollAwayIntent() &&
    el.scrollTop < prevScrollTopRef.current &&
    el.scrollHeight - el.scrollTop - el.clientHeight > 1
  ), [hasRecentUserScrollAwayIntent])

  // Detect user scroll direction to toggle auto-scroll.
  // Only three cases matter:
  //   1. At bottom → clear "scrolled up" (always)
  //   2. User-initiated moving up → set "scrolled up" (user scrolled away)
  //   3. Moving down but not at bottom → no-op (mid-animation)
  //
  // Direction alone is not enough. Browser layout, scroll anchoring, content
  // shrinkage, and programmatic scrolls can all reduce scrollTop without user
  // intent. Treating those as manual scroll-up leaves the chat stuck above the
  // latest message until the user clicks the affordance.
  const handleScroll = useCallback(() => {
    if (scrollThrottleRef.current) return
    scrollThrottleRef.current = requestAnimationFrame(() => {
      scrollThrottleRef.current = 0
      const el = scrollContainerRef.current
      if (!el) return

      const currentScrollTop = el.scrollTop
      const previousScrollTop = prevScrollTopRef.current
      const isScrollingUp = currentScrollTop < previousScrollTop
      prevScrollTopRef.current = currentScrollTop

      const distanceFromBottom = el.scrollHeight - currentScrollTop - el.clientHeight
      const atBottom = distanceFromBottom <= 50
      const hasRecentScrollAway = hasRecentUserScrollAwayIntent()

      if (atBottom) {
        if (isUserScrolledUpRef.current) {
          isUserScrolledUpRef.current = false
          setIsUserScrolledUp(false)
          scrollLog({ event: 'reached-bottom', distanceFromBottom })
        }
      } else if (isScrollingUp && hasRecentScrollAway && !isUserScrolledUpRef.current) {
        isUserScrolledUpRef.current = true
        setIsUserScrolledUp(true)
        scrollLog({ event: 'user-scrolled-up', distanceFromBottom, scrollTop: currentScrollTop })
      } else if (isScrollingUp && !hasRecentScrollAway) {
        scrollLog({
          event: 'non-user-scroll-up-ignored',
          trace_id: `director-chat-scroll:${projectId}`,
          projectId,
          project_id: projectId,
          distanceFromBottom,
          scrollTop: currentScrollTop,
          previousScrollTop,
        })
      }
    })
  }, [hasRecentUserScrollAwayIntent, projectId])

  // Attach scroll and user-input listeners to the scroll container.
  // Re-run when isLoading changes so we attach after the loading spinner is replaced
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const markWheelIntent = (event: WheelEvent) => markUserScrollIntent('wheel', event.deltaY < 0)
    const markTouchStartIntent = (event: TouchEvent) => {
      lastTouchYRef.current = event.touches[0]?.clientY ?? null
      markUserScrollIntent('touchstart')
    }
    const markTouchMoveIntent = (event: TouchEvent) => {
      const currentY = event.touches[0]?.clientY
      const previousY = lastTouchYRef.current
      if (currentY == null) return
      lastTouchYRef.current = currentY
      markUserScrollIntent('touchmove', previousY != null && currentY > previousY)
    }
    const markPointerDownIntent = (event: PointerEvent) => {
      lastPointerYRef.current = event.clientY
      markUserScrollIntent('pointerdown')
    }
    const markPointerMoveIntent = (event: PointerEvent) => {
      if (event.buttons === 0 || lastPointerYRef.current == null) return
      const previousY = lastPointerYRef.current
      lastPointerYRef.current = event.clientY
      markUserScrollIntent('pointerdrag', event.clientY < previousY)
    }
    const clearPointerIntent = () => {
      lastPointerYRef.current = null
    }
    const markKeyboardIntent = (event: KeyboardEvent) => {
      const target = event.target
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }

      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
        const scrollAway = ['ArrowUp', 'PageUp', 'Home'].includes(event.key) || (event.key === ' ' && event.shiftKey)
        markUserScrollIntent('keyboard', scrollAway)
      }
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    el.addEventListener('wheel', markWheelIntent, { passive: true })
    el.addEventListener('touchstart', markTouchStartIntent, { passive: true })
    el.addEventListener('touchmove', markTouchMoveIntent, { passive: true })
    el.addEventListener('pointerdown', markPointerDownIntent, { passive: true })
    el.addEventListener('pointermove', markPointerMoveIntent, { passive: true })
    el.addEventListener('pointerup', clearPointerIntent, { passive: true })
    el.addEventListener('pointercancel', clearPointerIntent, { passive: true })
    el.addEventListener('keydown', markKeyboardIntent)
    return () => {
      el.removeEventListener('scroll', handleScroll)
      el.removeEventListener('wheel', markWheelIntent)
      el.removeEventListener('touchstart', markTouchStartIntent)
      el.removeEventListener('touchmove', markTouchMoveIntent)
      el.removeEventListener('pointerdown', markPointerDownIntent)
      el.removeEventListener('pointermove', markPointerMoveIntent)
      el.removeEventListener('pointerup', clearPointerIntent)
      el.removeEventListener('pointercancel', clearPointerIntent)
      el.removeEventListener('keydown', markKeyboardIntent)
      if (scrollThrottleRef.current) {
        cancelAnimationFrame(scrollThrottleRef.current)
        scrollThrottleRef.current = 0
      }
    }
  }, [handleScroll, isLoading, markUserScrollIntent])

  const bottomAnchorFields = useCallback(() => ({
    trace_id: `director-chat-scroll:${projectId}`,
    projectId,
    project_id: projectId,
    hasTaskActivity,
    runningCount: runningTaskCount,
    queuedCount: queuedTaskCount,
    activeDirectorToolCount,
    isDirectorActive,
    showDirectorIndicator,
    executionPlanBatchCount: executionPlanBatches.length,
    visibleQuestionCount: visibleQuestions.length,
    errorVisible: !!error && !dismissedError,
    queuedPromptCount: queuedPrompts.length,
  }), [
    activeDirectorToolCount,
    dismissedError,
    error,
    executionPlanBatches.length,
    hasTaskActivity,
    isDirectorActive,
    showDirectorIndicator,
    projectId,
    queuedPrompts.length,
    queuedTaskCount,
    runningTaskCount,
    visibleQuestions.length,
  ])

  const restoreBottomAnchor = useCallback((reason: string) => {
    const el = scrollContainerRef.current
    const baseFields = bottomAnchorFields()

    if (!el) {
      scrollLog({ event: 'bottom-anchor:skipped', ...baseFields, reason: 'no_container' })
      return false
    }
    if (!initialScrollDoneRef.current) {
      scrollLog({ event: 'bottom-anchor:skipped', ...baseFields, reason: 'initial_not_done' })
      return false
    }
    if (isUserScrolledUpRef.current) {
      scrollLog({
        event: 'bottom-anchor:skipped',
        ...baseFields,
        reason: 'user_scrolled_up',
        scrollTop: Math.round(el.scrollTop),
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
        distanceBefore: Math.round(el.scrollHeight - el.scrollTop - el.clientHeight),
      })
      return false
    }
    if (hasPendingUserScrollAway(el)) {
      scrollLog({
        event: 'bottom-anchor:skipped',
        ...baseFields,
        reason: 'pending_user_scroll_away',
        scrollTop: Math.round(el.scrollTop),
        previousScrollTop: Math.round(prevScrollTopRef.current),
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
        distanceBefore: Math.round(el.scrollHeight - el.scrollTop - el.clientHeight),
      })
      return false
    }

    const distanceBefore = el.scrollHeight - el.scrollTop - el.clientHeight
    const roundedDistanceBefore = Math.round(distanceBefore)
    scrollLog({
      event: 'bottom-anchor:layout-change',
      ...baseFields,
      reason,
      scrollTop: Math.round(el.scrollTop),
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      distanceBefore: roundedDistanceBefore,
    })

    if (distanceBefore <= 1) {
      scrollLog({
        event: 'bottom-anchor:skipped',
        ...baseFields,
        reason: 'already_at_bottom',
        scrollTop: Math.round(el.scrollTop),
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
        distanceBefore: roundedDistanceBefore,
      })
      return false
    }

    el.scrollTop = el.scrollHeight
    prevScrollTopRef.current = el.scrollTop
    scrollLog({
      event: 'bottom-anchor:restored',
      ...baseFields,
      reason,
      scrollTop: Math.round(el.scrollTop),
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      distanceBefore: roundedDistanceBefore,
      distanceAfter: Math.round(el.scrollHeight - el.scrollTop - el.clientHeight),
    })
    return true
  }, [bottomAnchorFields, hasPendingUserScrollAway])

  // Keep the latest chat anchored when non-message chrome below the scroller
  // appears or disappears. The running-task card, execution-plan stack, error
  // card, question cards, and queued prompts are siblings of the scroll region;
  // they can shrink its clientHeight without changing messages.length. Browsers
  // preserve the absolute scrollTop in that case, which leaves the latest
  // message below the viewport unless we restore the bottom anchor.
  useLayoutEffect(() => {
    restoreBottomAnchor('layout_chrome_changed')
  }, [
    activeDirectorToolCount,
    dismissedError,
    error,
    executionPlanBatches.length,
    hasTaskActivity,
    isDirectorActive,
    showDirectorIndicator,
    projectId,
    queuedPrompts.length,
    queuedTaskCount,
    restoreBottomAnchor,
    runningTaskCount,
    visibleQuestions.length,
  ])

  // Message content can grow or shrink without changing message count: streamed
  // chunks, finalized content replacing a stream placeholder, markdown/image
  // load, and browser font/layout recalculation all mutate scrollHeight. Observe
  // the scroll viewport and its content so non-user resizes preserve the bottom
  // anchor under one shared contract.
  useEffect(() => {
    const viewport = scrollContainerRef.current
    const content = scrollContentRef.current
    if (!viewport || !content || typeof ResizeObserver === 'undefined') return

    let frame = 0
    const observer = new ResizeObserver(() => {
      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = 0
        restoreBottomAnchor('resize_observer')
      })
    })
    observer.observe(viewport)
    observer.observe(content)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [isLoading, restoreBottomAnchor])

  // Auto-scroll when messages change or when answering a question reveals
  // previously gated later history without changing the raw message count.
  useEffect(() => {
    const messageCount = messages.length
    const displayMessageCount = displayMessages.length
    const previousMessageCount = prevMessageCountRef.current
    const previousDisplayMessageCount = prevDisplayMessageCountRef.current
    const previousFirstUnanswered = prevFirstUnansweredMessageIndexRef.current

    const isInitialLoad = !initialScrollDoneRef.current && messageCount > 0
    const hasNewMessages = messageCount > previousMessageCount
    const visibleHistoryExpanded =
      displayMessageCount > previousDisplayMessageCount &&
      messageCount === previousMessageCount &&
      previousDisplayMessageCount > 0
    const questionGateReleased =
      previousFirstUnanswered !== -1 &&
      firstUnansweredMessageIndex === -1 &&
      visibleHistoryExpanded

    if (isInitialLoad) {
      scrollToBottom('instant')
      initialScrollDoneRef.current = true
      scrollLog({
        event: 'initial-scroll-bottom',
        projectId,
        project_id: projectId,
        messageCount,
        next_message_count: messageCount,
        displayMessageCount,
        ui_message_count_after: displayMessageCount,
      })
    } else if ((hasNewMessages || visibleHistoryExpanded) && !isUserScrolledUpRef.current) {
      scrollLog({
        event: visibleHistoryExpanded ? 'visible-history-expanded' : 'new-message-auto-scroll',
        trace_id: `director-chat-scroll:${projectId}`,
        projectId,
        project_id: projectId,
        messageCount,
        next_message_count: messageCount,
        previousMessageCount,
        previous_message_count: previousMessageCount,
        displayMessageCount,
        ui_message_count_after: displayMessageCount,
        previousDisplayMessageCount,
        ui_message_count_before: previousDisplayMessageCount,
        firstUnansweredMessageIndex,
        previousFirstUnanswered,
        questionGateReleased,
        reason: questionGateReleased ? 'question_gate_released' : 'visible_message_count_increased',
        status_before: previousFirstUnanswered === -1 ? 'all_visible' : 'question_gated',
        status_after: firstUnansweredMessageIndex === -1 ? 'all_visible' : 'question_gated',
      })
      scrollToBottom()
    }

    prevMessageCountRef.current = messageCount
    prevDisplayMessageCountRef.current = displayMessageCount
    prevFirstUnansweredMessageIndexRef.current = firstUnansweredMessageIndex
  }, [
    displayMessages.length,
    firstUnansweredMessageIndex,
    messages.length,
    projectId,
    scrollToBottom,
  ])

  // Auto-scroll during streaming (throttled to avoid jank)
  const lastStreamScrollRef = useRef(0)
  useEffect(() => {
    if (isUserScrolledUpRef.current) return
    const lastMsg = displayMessages[displayMessages.length - 1]
    if (lastMsg?.isStreaming && lastMsg.content) {
      const now = Date.now()
      if (now - lastStreamScrollRef.current > 300) {
        lastStreamScrollRef.current = now
        scrollToBottom()
      }
    }
  }, [displayMessages, scrollToBottom])

  // Scroll to show questions when they appear
  useEffect(() => {
    if (!isUserScrolledUpRef.current && effectiveQuestions.length > 0) {
      scrollToBottom()
    }
  }, [effectiveQuestions.length, scrollToBottom])

  // Auto-start with initial prompt if provided.
  // If pendingActions are present (sequel flow), execute them deterministically
  // (sequential fetch to execute-tool, no LLM) BEFORE firing the Director's
  // first prompt. The user sees imported nodes on the canvas when the Director
  // speaks its first message. Only fires if conversation is empty (prevents
  // duplicate on page refresh).
  useEffect(() => {
    // Wait for the project config query to settle so the first /agent/connect
    // carries the persisted axis-3 content_language (not Auto) — see the
    // useProjectConfig/useAgentChat wiring above.
    if (
      !initialPrompt ||
      hasStartedRef.current ||
      isLoading ||
      isProjectConfigLoading ||
      status !== 'idle' ||
      messages.length !== 0
    )
      return
    hasStartedRef.current = true

    const run = async () => {
      // Execute deterministic pending actions first (e.g. use_from_library
      // for each sequel import). Best-effort: skip failures, continue.
      if (pendingActions && pendingActions.length > 0) {
        const apiBase = getConfiguredApiBase()
        for (const action of pendingActions) {
          try {
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), 30_000)
            const resp = await authenticatedFetch(
              `${apiBase}/projects/${projectId}/agentic/director/tools/${action.tool}`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(action.args),
                signal: controller.signal,
              },
            )
            clearTimeout(timeout)
            if (!resp.ok) {
              // Non-2xx — skip this import, continue with the rest.
              // The Director's context message can note what actually landed.
            }
          } catch {
            // Best-effort: skip this import (network error, timeout, abort),
            // continue with the rest.
          }
        }
      }
      // Now fire the Director's first turn with the seeded context.
      startWithPrompt(initialPrompt, initialReferencedNodeIds, undefined, {
        selectedSkill: initialSelectedSkill,
        selectedSkillPosition: initialSelectedSkill?.position,
        replayScenario: activeReplayScenario,
      })
      // Clear location.state so refresh doesn't re-fire.
      window.history.replaceState({}, document.title)
    }
    run()
  }, [
    initialPrompt,
    initialSelectedSkill,
    initialReferencedNodeIds,
    activeReplayScenario,
    pendingActions,
    isLoading,
    isProjectConfigLoading,
    status,
    startWithPrompt,
    messages.length,
    projectId,
  ])

  // Release one FIFO item per Director active -> inactive transition. Broad
  // conversation status may remain `running` while worker nodes continue, so
  // it is deliberately not the release signal (RFC-0289).
  useEffect(() => {
    const previous = previousQueueDirectorActivityRef.current
    const scopeChanged = previous.scopeKey !== chatUiScopeKey
    if (scopeChanged) {
      previousQueueDirectorActivityRef.current = {
        scopeKey: chatUiScopeKey,
        active: isDirectorActive,
      }
      return
    }

    // A real question card pauses automatic release. Keep the completed turn
    // edge pending so clearing the question can release the FIFO head without
    // requiring an unrelated second activity transition.
    if (
      previous.active &&
      !isDirectorActive &&
      hasRealQuestions &&
      queuedPromptsRef.current.length > 0
    ) {
      return
    }

    previousQueueDirectorActivityRef.current = {
      scopeKey: chatUiScopeKey,
      active: isDirectorActive,
    }
    if (!previous.active || isDirectorActive || hasRealQuestions) return

    const [next, ...rest] = queuedPromptsRef.current
    if (!next) return

    queueActivityCycleSequenceRef.current += 1
    const activityCycleSequence = queueActivityCycleSequenceRef.current
    replaceQueuedPrompts(rest)
    promptQueueLog({
      event: 'prompt-queue:auto-dequeued',
      trace_id: `chat-prompt-queue:${chatUiScopeKey}`,
      scope_key: chatUiScopeKey,
      requested: 'release-next-prompt',
      resolved: 'next-director-turn-requested',
      outcome: 'dequeued',
      prompt_id: next.id,
      target_id: next.id,
      edit_revision: next.editRevision,
      dequeue_order: 'fifo',
      activity_cycle_sequence: activityCycleSequence,
      sequence_number: activityCycleSequence,
      trigger: 'director-active-to-inactive',
      queue_length_before: rest.length + 1,
      queue_length_after: rest.length,
      remaining_count: rest.length,
      director_was_active: true,
      director_active_at_release: false,
      next_turn_requested: true,
      worker_activity_present: hasTaskActivity,
      stop_requested: false,
    })
    const composerSubmission = { attachmentCount: next.attachments?.length ?? 0 }
    if (next.selectedSkill) {
      startWithPrompt(next.message, next.referencedNodeIds, undefined, {
        selectedSkill: next.selectedSkill,
        selectedSkillPosition: next.selectedSkillPosition,
        composerSubmission,
      })
    } else {
      startWithPrompt(next.message, next.referencedNodeIds, undefined, { composerSubmission })
    }
  }, [
    chatUiScopeKey,
    hasRealQuestions,
    hasTaskActivity,
    isDirectorActive,
    replaceQueuedPrompts,
    startWithPrompt,
  ])

  // Reset dismissed error when a new error occurs
  useEffect(() => {
    if (error) {
      setDismissedError(false)
    }
  }, [error])

  // A rejected response returns the chat to an actionable state without
  // changing the pending question objects. Re-enable the preserved draft so
  // the user can retry instead of leaving the batch permanently disabled.
  useEffect(() => {
    if (status !== 'running') {
      setSubmittingQuestionIds(new Set())
    }
  }, [status])

  // Clear submitting state and selections only when the question batch
  // identity changes. A same-ID authoritative refresh must preserve a draft
  // after an uncommitted/ambiguous submission failure.
  useEffect(() => {
    setSubmittingQuestionIds(new Set())
    setSelectedAnswers({})
    setAnswerDrafts({})
    setCustomTexts({})
    setSkippedQuestionIds(new Set())
  }, [effectiveQuestionIds])

  // Listen for story structure events from canvas
  useEffect(() => {
    // Legacy handler for storyStructure node (if still used)
    const handleGenerateScenes = (event: Event) => {
      if (!isGenerateScenesEvent(event)) return
      const detail = event.detail
      if (status === 'running') return

      const cards = detail.cards || []
      const cardSummary = cards
        .map((card: { sceneNumber: number; purpose: string; storyBeat: string }) =>
          `Scene ${card.sceneNumber} (${card.purpose}): ${card.storyBeat}`)
        .join('\n')

      const message = `Generate scenes from my story structure:\n\nTone: ${detail.tone || 'Cinematic'}\nHook: ${detail.hook?.text || 'N/A'}\n\nStructure:\n${cardSummary}`

      startWithPrompt(message)
    }

    const handleCardAction = (event: Event) => {
      if (!isCardActionEvent(event)) return
      const detail = event.detail
      if (status === 'running') return

      const actionMap: Record<string, string> = {
        'regen': 'Please regenerate',
        'shorter': 'Please make this shorter',
        'expand': 'Please expand with more detail',
      }
      const actionText = detail.action ? actionMap[detail.action] || detail.action : 'Update'

      const message = `${actionText} for Scene ${detail.card?.sceneNumber} (${detail.card?.purpose}):\n\n"${detail.card?.storyBeat}"\n\nContext: ${detail.context?.tone || 'Cinematic'} tone`

      startWithPrompt(message)
    }

    window.addEventListener('generate-scenes-from-structure', handleGenerateScenes)
    window.addEventListener('story-structure:card-action', handleCardAction)

    return () => {
      window.removeEventListener('generate-scenes-from-structure', handleGenerateScenes)
      window.removeEventListener('story-structure:card-action', handleCardAction)
    }
  }, [status, startWithPrompt])

  // Listen for agent-chat:send events (e.g., from story direction approval)
  useEffect(() => {
    const handleChatSend = (event: Event) => {
      if (!isAgentChatSendEvent(event)) return
      const detail = event.detail
      if (status === 'running') return
      if (detail?.message) {
        startWithPrompt(detail.message, detail.referencedNodeIds)
        // Notify the story direction node that approval was triggered via chat
        window.dispatchEvent(new CustomEvent('story-direction:approved-via-chat'))
      }
    }
    window.addEventListener('agent-chat:send', handleChatSend)
    return () => window.removeEventListener('agent-chat:send', handleChatSend)
  }, [status, startWithPrompt])

  // Listen for add-node-to-chat events (e.g., from node toolbar "Add to Chat" button)
  useEffect(() => {
    return listenAppEvent('add-node-to-chat', (detail) => {
      if (detail?.nodeId) {
        // Small delay to let sidebar open animation complete
        setTimeout(() => {
          noteChatReference(detail.nodeId, detail.source ?? 'add_to_chat')
          chatInputRef.current?.addMention(detail.nodeId)
        }, 100)
      }
    })
  }, [])

  const handleOptionSelect = useCallback((
    questionId: string,
    option: string,
    deferClarificationSubmit = false,
  ) => {
    const question = effectiveQuestions.find(q => getAnswerKey(q) === questionId)
    if (!question) return
    const optionMeta = question.options.find((candidate) => (candidate.value || candidate.label) === option)

    // Update local selection immediately for visual feedback
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: option }))
    const updatedAnswerDrafts = {
      ...answerDrafts,
      [questionId]: { selected_option: option },
    }
    setAnswerDrafts(updatedAnswerDrafts)
    // Clear custom text so the clicked option takes priority
    setCustomTexts((prev) => ({ ...prev, [questionId]: '' }))
    // Answering a previously skipped page replaces the skipped state.
    setSkippedQuestionIds((previous) => {
      if (!previous.has(questionId)) return previous
      const next = new Set(previous)
      next.delete(questionId)
      return next
    })

    // Check if all questions in THIS clarification block will be answered after
    // this selection. Scheduling (cost/funds) questions live on a separate
    // surface and must not gate the clarification auto-submit — otherwise
    // answering while a cost/plan card is co-pending never fires
    // (post 1584335133323784).
    const updatedCustomTexts = { ...customTexts, [questionId]: '' }
    const gatingQuestions = clarificationGatingQuestions(effectiveQuestions)
    const allAnswered = gatingQuestions.every(q => {
      const key = getAnswerKey(q)
      return !!answerValueFromDraft(key, updatedAnswerDrafts, updatedCustomTexts)
    })
    const answeredCount = gatingQuestions.filter(q => {
      const key = getAnswerKey(q)
      return !!answerValueFromDraft(key, updatedAnswerDrafts, updatedCustomTexts)
    }).length

    questionCardLog({
      event: 'question-card:option_select',
      projectId,
      questionId,
      questionType: question.question_type,
      option,
      optionLabel: optionMeta?.label ?? option,
      optionCount: question.options.length,
      questionCount: effectiveQuestions.length,
      gatingQuestionCount: gatingQuestions.length,
      answeredCount,
      allAnswered,
      schedulingQuestion: isSchedulingQuestionId(questionId),
    })

    const isSchedulingQuestion = isSchedulingQuestionId(questionId)

    // Scheduling cards are independent actions, not clarification batches.
    // Submit the clicked card itself so Apply/Cancel never becomes an empty
    // payload after clarification-only gating filters run.
    if (isSchedulingQuestion) {
      setTimeout(() => {
        const submittedQuestionIds = [questionId]
        setSubmittingQuestionIds(prev => {
          const next = new Set(prev)
          submittedQuestionIds.forEach((id) => next.add(id))
          return next
        })

        const answers: Record<string, AnswerValue> = {
          [questionId]: { selected_option: option },
        }
        questionCardLog({
          event: 'question-card:auto_submit',
          projectId,
          triggerQuestionId: questionId,
          answerCount: 1,
          answerKeys: questionId,
          selectedOptions: selectedOptionsSummary(answers),
          schedulingOnly: true,
        })

        void Promise.resolve(respondToQuestions(answers)).finally(() => {
          clearSubmittingQuestions(submittedQuestionIds)
        })
      }, 150)
      return
    }

    if (deferClarificationSubmit) return

    // Only auto-submit if ALL questions in this clarification block are answered
    if (allAnswered) {
      setTimeout(() => {
        // Mark this block's questions as submitting
        const submittedQuestionIds = gatingQuestions.map(getAnswerKey)
        setSubmittingQuestionIds(prev => {
          const next = new Set(prev)
          submittedQuestionIds.forEach((id) => next.add(id))
          return next
        })

        // Build answers for this clarification block and submit together
        const answers: Record<string, AnswerValue> = {}
        for (const q of gatingQuestions) {
          const key = getAnswerKey(q)
          const answer = answerValueFromDraft(key, updatedAnswerDrafts, updatedCustomTexts)
          if (answer) {
            answers[key] = answer
          }
        }

        const answerKeys = Object.keys(answers)
        questionCardLog({
          event: 'question-card:auto_submit',
          projectId,
          triggerQuestionId: questionId,
          answerCount: answerKeys.length,
          answerKeys: answerKeys.join(','),
          selectedOptions: selectedOptionsSummary(answers),
          schedulingOnly: answerKeys.length > 0 && answerKeys.every(isSchedulingQuestionId),
        })

        void Promise.resolve(respondToQuestions(answers)).finally(() => {
          clearSubmittingQuestions(submittedQuestionIds)
        })
      }, 150)
    }
  }, [
    answerDrafts,
    clearSubmittingQuestions,
    customTexts,
    effectiveQuestions,
    projectId,
    respondToQuestions,
  ])

  const handleStructuredQuestionAnswer = useCallback((
    questionId: string,
    answer: AnswerValue,
  ) => {
    const question = effectiveQuestions.find(q => getAnswerKey(q) === questionId)
    if (!question) return

    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answer.selected_option }))
    const updatedAnswerDrafts = { ...answerDrafts, [questionId]: answer }
    setAnswerDrafts(updatedAnswerDrafts)
    setCustomTexts((prev) => ({ ...prev, [questionId]: answer.custom_text ?? '' }))
    setSkippedQuestionIds((previous) => {
      if (!previous.has(questionId)) return previous
      const next = new Set(previous)
      next.delete(questionId)
      return next
    })

    const updatedCustomTexts = { ...customTexts, [questionId]: answer.custom_text ?? '' }
    const gatingQuestions = clarificationGatingQuestions(effectiveQuestions)
    const schedulingExcludedCount = effectiveQuestions.length - gatingQuestions.length
    const answers: Record<string, AnswerValue> = {}

    for (const q of gatingQuestions) {
      const key = getAnswerKey(q)
      const draft = answerValueFromDraft(key, updatedAnswerDrafts, updatedCustomTexts)
      if (draft) {
        answers[key] = draft
      }
    }

    const answerKeys = Object.keys(answers)
    const unansweredQuestions = gatingQuestions.filter(q => !answers[getAnswerKey(q)])
    const allAnswered = unansweredQuestions.length === 0

    setTimeout(() => {
      if (!allAnswered) {
        const nextUnansweredKey = getAnswerKey(unansweredQuestions[0])
        const nextUnansweredCard = questionRefs.current.get(nextUnansweredKey)
        nextUnansweredCard?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        nextUnansweredCard?.querySelector<HTMLElement>(ANSWER_FIELD_SELECTOR)?.focus()
        questionCardLog({
          event: 'question-card:structured_deferred',
          projectId,
          questionId,
          questionType: question.question_type,
          answerCount: answerKeys.length,
          answerKeys: answerKeys.join(','),
          questionCount: effectiveQuestions.length,
          gatingQuestionCount: gatingQuestions.length,
          schedulingExcludedCount,
          unansweredQuestionCount: unansweredQuestions.length,
          selectedOptions: selectedOptionsSummary(answers),
          toolResultContentLength: answer.tool_result_content?.length ?? 0,
        })
        return
      }

      const submittedQuestionIds = gatingQuestions.map(getAnswerKey)
      setSubmittingQuestionIds(prev => {
        const next = new Set(prev)
        submittedQuestionIds.forEach((id) => next.add(id))
        return next
      })

      questionCardLog({
        event: 'question-card:structured_submit',
        projectId,
        questionId,
        questionType: question.question_type,
        answerCount: answerKeys.length,
        answerKeys: answerKeys.join(','),
        questionCount: effectiveQuestions.length,
        gatingQuestionCount: gatingQuestions.length,
        schedulingExcludedCount,
        selectedOptions: selectedOptionsSummary(answers),
        toolResultContentLength: answer.tool_result_content?.length ?? 0,
      })

      void Promise.resolve(respondToQuestions(answers)).finally(() => {
        clearSubmittingQuestions(submittedQuestionIds)
      })
    }, 150)
  }, [
    answerDrafts,
    clearSubmittingQuestions,
    customTexts,
    effectiveQuestions,
    projectId,
    respondToQuestions,
  ])

  const handleBatchedQuestionSkip = useCallback((questionId: string) => {
    setSelectedAnswers((previous) => {
      if (!previous[questionId]) return previous
      const next = { ...previous }
      delete next[questionId]
      return next
    })
    setCustomTexts((previous) => {
      if (!previous[questionId]) return previous
      const next = { ...previous }
      delete next[questionId]
      return next
    })
    setAnswerDrafts((previous) => {
      if (!previous[questionId]) return previous
      const next = { ...previous }
      delete next[questionId]
      return next
    })
    setSkippedQuestionIds((previous) => {
      if (previous.has(questionId)) return previous
      return new Set(previous).add(questionId)
    })
    questionCardLog({
      event: 'question-batch:question-skipped',
      projectId,
      questionId,
    })
  }, [projectId])

  const handleBatchedQuestionsSubmit = useCallback(() => {
    const gatingQuestions = clarificationGatingQuestions(effectiveQuestions)
    const answers: Record<string, AnswerValue> = {}
    for (const question of gatingQuestions) {
      const key = getAnswerKey(question)
      const answer = answerValueFromDraft(key, answerDrafts, customTexts)
      if (answer) {
        answers[key] = answer
      }
    }
    const incompleteEarlierQuestions = gatingQuestions.slice(0, -1).filter((question) => {
      const key = getAnswerKey(question)
      return !answers[key] && !skippedQuestionIds.has(key)
    })
    if (incompleteEarlierQuestions.length > 0) {
      questionCardLog({
        event: 'question-batch:submit-rejected',
        projectId,
        answerCount: Object.keys(answers).length,
        questionCount: gatingQuestions.length,
        incompleteQuestionIds: incompleteEarlierQuestions.map(getAnswerKey).join(','),
      })
      return
    }

    const skippedQuestionKeys = gatingQuestions
      .map(getAnswerKey)
      .filter((key) => !answers[key])

    const submittedQuestionIds = gatingQuestions.map(getAnswerKey)
    setSubmittingQuestionIds((previous) => {
      const next = new Set(previous)
      submittedQuestionIds.forEach((id) => next.add(id))
      return next
    })
    questionCardLog({
      event: 'question-batch:submitted',
      projectId,
      answerCount: Object.keys(answers).length,
      answerKeys: Object.keys(answers).join(','),
      skippedCount: skippedQuestionKeys.length,
      skippedQuestionIds: skippedQuestionKeys.join(','),
      selectedOptions: selectedOptionsSummary(answers),
    })
    if (skippedQuestionKeys.length > 0) {
      void Promise.resolve(respondToQuestions(answers, {
        completeQuestionBatch: true,
        skippedQuestionIds: skippedQuestionKeys,
      })).finally(() => {
        clearSubmittingQuestions(submittedQuestionIds)
      })
    } else {
      void Promise.resolve(
        respondToQuestions(answers, { deferOptimisticQuestionAnswers: true }),
      ).finally(() => {
        clearSubmittingQuestions(submittedQuestionIds)
      })
    }
  }, [
    answerDrafts,
    clearSubmittingQuestions,
    customTexts,
    effectiveQuestions,
    projectId,
    respondToQuestions,
    skippedQuestionIds,
  ])

  // Handle sequel "Create new project" button click. Answers the question
  // (so the source Director knows the outcome), creates the new project, and
  // navigates to it with the seeded context + deterministic pending actions.
  const handleCreateProjectFromProposal = useCallback(
    async (questionKey: string, meta: ChatEventCreateProjectMeta) => {
      // Narrow production_target/aspect_ratio to their literal types without
      // forbidden casts (the repo rejects `as`). Only pass when they match
      // the accepted literal union values.
      const pt = meta.production_target
      const ar = meta.aspect_ratio
      const validPt = pt === 'video' || pt === 'webtoon' ? pt : undefined
      const validAr = ar === '16:9' || ar === '9:16' ? ar : undefined

      try {
        const newProject = await createProject.mutateAsync({
          name: meta.name,
          // UI locale so the backend can localize any strings it generates
          // for the new project (default name / AI title if `name` is empty)
          // (#2340). Explicit param — S1-1 (never read locale from JWT
          // user_metadata).
          ui_locale: normalizeLocale(i18n.language) ?? 'en',
          ...(meta.style ? { style: meta.style } : {}),
          ...(validPt ? { production_target: validPt } : {}),
          ...(validAr ? { aspect_ratio: validAr } : {}),
        })
        // Navigate to the new workspace with:
        // - initialPrompt: context-only (describes what was imported, not
        //   an instruction to import — imports run deterministically).
        // - pendingActions: the deterministic import actions that fire before
        //   the Director's first turn (execute-tool, no LLM involved).
        const pendingNavActions = (meta.library_node_ids || []).map((nodeId) => ({
          tool: 'use_from_library',
          args: { library_node_id: nodeId },
        }))
        sequelNavigate(`/project/${newProject.id}/workspace`, {
          state: { initialPrompt: meta.context, pendingActions: pendingNavActions },
        })
        // Answer AFTER successful creation + navigation so the source Director
        // only learns "create" when the sequel actually exists. If creation
        // failed, the card stays actionable.
        handleOptionSelect(questionKey, 'create')
      } catch {
        // Creation failed — the user stays in the current project with the
        // card still actionable. The error is surfaced by useCreateProject's
        // error state / toast. Don't answer the question so the user can retry.
      }
    },
    [handleOptionSelect, createProject, sequelNavigate, i18n.language],
  )

  // RFC 0053: Handle custom text change (mutually exclusive with selection)
  //
  // Bail out when the text is unchanged. Spreading unconditionally allocated a
  // new object on every call, so React could never skip the re-render — which
  // let a no-op echo from the mention composer drive an unbounded render loop
  // (post 1597143022042995). Returning `prev` makes the write idempotent, so
  // even a chatty caller cannot spin the tree.
  const handleCustomTextChange = useCallback((questionId: string, text: string) => {
    setCustomTexts((prev) => (prev[questionId] === text ? prev : { ...prev, [questionId]: text }))
    if (text.trim()) {
      setSkippedQuestionIds((previous) => {
        if (!previous.has(questionId)) return previous
        const next = new Set(previous)
        next.delete(questionId)
        return next
      })
    } else {
      setAnswerDrafts((previous) => {
        if (!previous[questionId]?.custom_text) return previous
        const next = { ...previous }
        delete next[questionId]
        return next
      })
    }
  }, [])

  // RFC 0053: Clear selection when user focuses on custom input (mutually exclusive)
  const handleCustomInputFocus = useCallback((questionId: string) => {
    const question = effectiveQuestions.find(q => getAnswerKey(q) === questionId)
    freeTextAnswerLog({
      event: 'question-card:custom_answer_focused',
      trace_id: `director-free-text-answers:${projectId}`,
      projectId,
      questionId,
      nodeId: questionId,
      questionType: question?.question_type ?? '',
      questionCount: effectiveQuestions.length,
      questionIndex: effectiveQuestions.findIndex(q => getAnswerKey(q) === questionId),
      schedulingQuestion: isSchedulingQuestionId(questionId),
    })
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: '' }))
    setAnswerDrafts((previous) => {
      if (!previous[questionId]) return previous
      const next = { ...previous }
      delete next[questionId]
      return next
    })
  }, [effectiveQuestions, projectId])

  // Submit custom text on Enter key
  const handleCustomSubmit = useCallback((questionId: string) => {
    const custom = customTexts[questionId]?.trim()
    if (!custom) return
    const question = effectiveQuestions.find(q => getAnswerKey(q) === questionId)

    // Only the clarification block this custom answer belongs to gates the
    // submit. Scheduling (cost/funds) questions live on a separate surface
    // (ExecutionPlanCard / "Let's go!") and must not force the answer to defer
    // forever — that dropped the user's typed input (post 1584335133323784).
    const gatingQuestions = clarificationGatingQuestions(effectiveQuestions)
    const schedulingExcludedCount = effectiveQuestions.length - gatingQuestions.length
    const updatedAnswerDrafts = {
      ...answerDrafts,
      [questionId]: { selected_option: 'user_feedback', custom_text: custom },
    }
    setAnswerDrafts(updatedAnswerDrafts)
    const answers: Record<string, AnswerValue> = {}
    for (const q of gatingQuestions) {
      const key = getAnswerKey(q)
      const answer = answerValueFromDraft(key, updatedAnswerDrafts, customTexts)
      if (answer) {
        answers[key] = answer
      }
    }
    const answerKeys = Object.keys(answers)
    const nonemptyCustomAnswerCount = Object.values(answers).filter(
      answer => Boolean(answer.custom_text?.trim()),
    ).length
    const unansweredQuestions = gatingQuestions.filter(q => !answers[getAnswerKey(q)])
    const unansweredQuestionCount = unansweredQuestions.length
    const allAnswered = unansweredQuestionCount === 0
    freeTextAnswerLog({
      event: allAnswered
        ? 'question-card:custom_answers_submitted'
        : 'question-card:custom_answers_deferred',
      trace_id: `director-free-text-answers:${projectId}`,
      projectId,
      questionId,
      nodeId: questionId,
      questionType: question?.question_type ?? '',
      customTextLength: custom.length,
      answerCount: answerKeys.length,
      totalQuestionCount: effectiveQuestions.length,
      gatingQuestionCount: gatingQuestions.length,
      schedulingExcludedCount,
      customAnswerCount: nonemptyCustomAnswerCount,
      nonemptyCustomAnswerCount,
      unansweredQuestionCount,
      validationResult: allAnswered ? 'accepted' : 'incomplete',
      requested: gatingQuestions.length,
      resolved: nonemptyCustomAnswerCount,
      success_count: nonemptyCustomAnswerCount,
      failed_count: unansweredQuestionCount,
      validity: allAnswered
        ? 'all_custom_answers_submitted'
        : 'custom_answers_waiting_for_remaining_questions',
      answerKeys: answerKeys.join(','),
      selectedOptions: selectedOptionsSummary(answers),
      schedulingOnly: answerKeys.length > 0 && answerKeys.every(isSchedulingQuestionId),
    })

    // All cards in one Director tool call share a base tool_use_id. Submitting
    // a subset would resume that tool call and make later answers duplicates.
    if (!allAnswered) {
      const nextUnansweredKey = getAnswerKey(unansweredQuestions[0])
      const nextUnansweredCard = questionRefs.current.get(nextUnansweredKey)
      nextUnansweredCard?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      // Matches the plain input AND the mention composer's contentEditable —
      // see ANSWER_FIELD_SELECTOR (post 1585245219899442).
      nextUnansweredCard?.querySelector<HTMLElement>(ANSWER_FIELD_SELECTOR)?.focus()
      return
    }

    const submittedQuestionIds = gatingQuestions.map(getAnswerKey)
    setSubmittingQuestionIds(prev => {
      const next = new Set(prev)
      submittedQuestionIds.forEach((id) => next.add(id))
      return next
    })
    void Promise.resolve(respondToQuestions(answers)).finally(() => {
      clearSubmittingQuestions(submittedQuestionIds)
    })
  }, [
    answerDrafts,
    clearSubmittingQuestions,
    customTexts,
    effectiveQuestions,
    projectId,
    respondToQuestions,
  ])

  const chatInputRef = useRef<ChatInputHandle>(null)

  const collectPartialAnswers = useCallback((): Record<string, string> | undefined => {
    if (effectiveQuestions.length === 0) return undefined
    const partials: Record<string, string> = {}
    for (const q of effectiveQuestions) {
      const key = getAnswerKey(q)
      const custom = customTexts[key]?.trim()
      const sel = selectedAnswers[key]
      const draft = answerDrafts[key]
      if (custom) {
        partials[key] = custom
      } else if (draft?.custom_text?.trim()) {
        partials[key] = draft.custom_text.trim()
      } else if (draft?.tool_result_content?.trim()) {
        partials[key] = draft.tool_result_content.trim()
      } else if (draft?.selected_option) {
        partials[key] = draft.selected_option
      } else if (sel) {
        partials[key] = sel
      }
    }
    return Object.keys(partials).length > 0 ? partials : undefined
  }, [answerDrafts, customTexts, effectiveQuestions, selectedAnswers])

  const handleSendMessage = useCallback((
    message: string,
    attachments?: ChatAttachment[],
    referencedNodeIds?: string[],
    selectedSkill?: DirectorSkillSummary,
    selectedSkillPosition?: number,
  ): ChatSendDisposition | false => {
    if (!message.trim() && (!attachments || attachments.length === 0)) return false

    isUserScrolledUpRef.current = false
    setIsUserScrolledUp(false)
    setDismissedError(true)
    const scopedReferences = mergeScopedReferences(referencedNodeIds, forcedReferencedNodeIds)
    const retrievedForEdit = retrievedPromptForEditRef.current
    if (!retrievedForEdit) queuedPromptSequenceRef.current += 1
    const promptId = retrievedForEdit?.id ??
      `prompt-${Date.now()}-${queuedPromptSequenceRef.current}`
    const editRevision = retrievedForEdit ? retrievedForEdit.editRevision + 1 : 0

    if (shouldQueuePrompt) {
      const item: QueuedPrompt = {
        id: promptId,
        editRevision,
        message,
        attachments,
        referencedNodeIds: scopedReferences,
        selectedSkill,
        selectedSkillPosition,
      }
      const previous = queuedPromptsRef.current
      const next = [...previous, item]
      replaceQueuedPrompts(next)
      retrievedPromptForEditRef.current = null
      promptQueueLog({
        event: 'prompt-queue:enqueued',
        trace_id: `chat-prompt-queue:${chatUiScopeKey}`,
        scope_key: chatUiScopeKey,
        requested: 'send-message',
        resolved: 'queued-for-next-director-turn',
        outcome: 'enqueued',
        prompt_id: promptId,
        target_id: promptId,
        original_prompt_id: retrievedForEdit?.id ?? null,
        edit_revision: editRevision,
        enqueue_order: 'fifo-tail',
        queue_length_before: previous.length,
        queue_length_after: next.length,
        remaining_count: next.length,
        director_active: isDirectorActive,
        has_real_questions: hasRealQuestions,
        message_length: message.length,
        referenced_node_count: scopedReferences?.length ?? 0,
        attachment_count: attachments?.length ?? 0,
        stop_requested: false,
      })
      return 'queued'
    }

    let partialAnswerMap: Record<string, string> | undefined
    if (effectiveQuestions.length > 0) {
      partialAnswerMap = collectPartialAnswers()
      dismissPendingQuestions(partialAnswerMap)
      setSelectedAnswers({})
      setAnswerDrafts({})
      setCustomTexts({})
      setSkippedQuestionIds(new Set())
    }
    promptQueueLog({
      event: 'prompt-queue:routed-immediately',
      trace_id: `chat-prompt-queue:${chatUiScopeKey}`,
      scope_key: chatUiScopeKey,
      requested: 'send-message',
      resolved: 'director-turn-started',
      outcome: 'sent',
      prompt_id: promptId,
      target_id: promptId,
      original_prompt_id: retrievedForEdit?.id ?? null,
      edit_revision: editRevision,
      queue_length_before: queuedPromptsRef.current.length,
      queue_length_after: queuedPromptsRef.current.length,
      director_active: isDirectorActive,
      has_real_questions: hasRealQuestions,
      worker_activity_present: hasTaskActivity,
      stop_requested: false,
    })
    retrievedPromptForEditRef.current = null
    const composerSubmission = { attachmentCount: attachments?.length ?? 0 }
    if (selectedSkill) {
      startWithPrompt(message, scopedReferences, partialAnswerMap, {
        selectedSkill,
        selectedSkillPosition,
        composerSubmission,
      })
    } else {
      startWithPrompt(message, scopedReferences, partialAnswerMap, { composerSubmission })
    }
    return 'sent'
  }, [
    chatUiScopeKey,
    startWithPrompt,
    effectiveQuestions,
    dismissPendingQuestions,
    collectPartialAnswers,
    forcedReferencedNodeIds,
    hasRealQuestions,
    hasTaskActivity,
    isDirectorActive,
    replaceQueuedPrompts,
    shouldQueuePrompt,
  ])

  const removeQueuedPrompt = useCallback((index: number) => {
    const previous = queuedPromptsRef.current
    const item = previous[index]
    if (!item) return
    const next = previous.filter((_, i) => i !== index)
    replaceQueuedPrompts(next)
    promptQueueLog({
      event: 'prompt-queue:removed',
      trace_id: `chat-prompt-queue:${chatUiScopeKey}`,
      scope_key: chatUiScopeKey,
      requested: 'remove-queued-prompt',
      resolved: 'removed',
      outcome: 'removed',
      prompt_id: item.id,
      target_id: item.id,
      edit_revision: item.editRevision,
      queue_length_before: previous.length,
      queue_length_after: next.length,
      remaining_count: next.length,
      stop_requested: false,
    })
  }, [chatUiScopeKey, replaceQueuedPrompts])

  // Move a queued prompt back to the text field for editing
  const editQueuedPrompt = useCallback((
    index: number,
    source: 'button' | 'arrow-up' = 'button',
  ): boolean => {
    const previous = queuedPromptsRef.current
    const item = previous[index]
    if (!item || !chatInputRef.current) return false

    chatInputRef.current.setText(item.message, {
      selectedSkill: item.selectedSkill ?? null,
      selectedSkillPosition: item.selectedSkillPosition,
    })
    retrievedPromptForEditRef.current = item
    const next = previous.filter((_, i) => i !== index)
    replaceQueuedPrompts(next)
    promptQueueLog({
      event: 'prompt-queue:retrieved-for-edit',
      trace_id: `chat-prompt-queue:${chatUiScopeKey}`,
      scope_key: chatUiScopeKey,
      requested: 'retrieve-queued-prompt',
      resolved: 'restored-to-composer',
      outcome: 'retrieved',
      prompt_id: item.id,
      target_id: item.id,
      edit_revision: item.editRevision,
      retrieval_source: source,
      edit_control_icon: source === 'button' ? 'pencil' : 'keyboard-arrow-up',
      queue_order: source === 'arrow-up' ? 'lifo' : 'selected',
      queue_length_before: previous.length,
      queue_length_after: next.length,
      remaining_count: next.length,
      referenced_node_count: item.referencedNodeIds?.length ?? 0,
      stop_requested: false,
    })
    return true
  }, [chatUiScopeKey, replaceQueuedPrompts])

  const retrieveNewestQueuedPrompt = useCallback((): boolean => {
    const newestIndex = queuedPromptsRef.current.length - 1
    if (newestIndex < 0) return false
    return editQueuedPrompt(newestIndex, 'arrow-up')
  }, [editQueuedPrompt])

  // Send a queued prompt immediately, skipping any pending questions.
  const sendQueuedPrompt = useCallback((index: number) => {
    if (isDirectorActive) return
    const previous = queuedPromptsRef.current
    const item = previous[index]
    if (!item) return
    const next = previous.filter((_, i) => i !== index)
    replaceQueuedPrompts(next)
    setDismissedError(true)
    let partialAnswerMap: Record<string, string> | undefined
    if (effectiveQuestions.length > 0) {
      partialAnswerMap = collectPartialAnswers()
      dismissPendingQuestions(partialAnswerMap)
      setSelectedAnswers({})
      setAnswerDrafts({})
      setCustomTexts({})
      setSkippedQuestionIds(new Set())
    }
    promptQueueLog({
      event: 'prompt-queue:manual-dequeued',
      trace_id: `chat-prompt-queue:${chatUiScopeKey}`,
      scope_key: chatUiScopeKey,
      requested: 'send-queued-prompt-now',
      resolved: 'director-turn-started',
      outcome: 'dequeued',
      prompt_id: item.id,
      target_id: item.id,
      edit_revision: item.editRevision,
      dequeue_order: 'selected',
      queue_length_before: previous.length,
      queue_length_after: next.length,
      remaining_count: next.length,
      director_active: false,
      stop_requested: false,
    })
    if (item.selectedSkill) {
      startWithPrompt(item.message, item.referencedNodeIds, partialAnswerMap, {
        selectedSkill: item.selectedSkill,
        selectedSkillPosition: item.selectedSkillPosition,
      })
    } else {
      startWithPrompt(item.message, item.referencedNodeIds, partialAnswerMap)
    }
  }, [
    chatUiScopeKey,
    startWithPrompt,
    effectiveQuestions,
    dismissPendingQuestions,
    collectPartialAnswers,
    isDirectorActive,
    replaceQueuedPrompts,
  ])

  // Match the normal Director question surface when a brand-new project has no
  // conversation yet. Keep the existing loading and state guards so it cannot
  // flash while history or another canvas is loading.
  const showWelcome =
    !isLoading &&
    messages.length === 0 &&
    displayMessages.length === 0 &&
    status === 'idle' &&
    !initialPrompt &&
    !error &&
    !hasRealQuestions

  useEffect(() => {
    if (showWelcome) welcomeLog('welcome_question_shown project=%s', projectId)
  }, [showWelcome, projectId])

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-chrome" />
      </div>
    )
  }

  return (
    <MentionRenderProvider value={mentionRenderValue}>
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Messages wrapper - relative for floating scroll button.
          ``min-h-0`` lets this flex child shrink below its intrinsic
          content height when sibling cards (running tasks, execution
          plans, errors, ChatInput) take vertical space — without it,
          the inner ``overflow-y-auto`` scroller is silently disabled. */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {/* Messages - display up to first unanswered question */}
        <div ref={scrollContainerRef} data-testid="chat-scroll-container" className="chat-scroll h-full overflow-y-auto overflow-x-hidden mr-1 px-4 py-4 focus-visible:ring-0 focus-visible:ring-offset-0">
          <div ref={scrollContentRef} className="space-y-4">
          {showWelcome && scopedWelcome && <p className="text-sm leading-relaxed text-muted-foreground">{scopedWelcome}</p>}
          {showWelcome && !scopedWelcome && (
            <QuestionCard
              question={t('agentChat.welcome.question')}
              options={welcomeSamplePrompts.map((prompt) => ({ label: prompt }))}
              onSelect={(prompt) => {
                setWelcomeCustomText('')
                welcomeLog('sample_prompt_prefill len=%d', prompt.length)
                chatInputRef.current?.setText(prompt)
              }}
              allowCustomInput
              customText={welcomeCustomText}
              onCustomTextChange={setWelcomeCustomText}
              onCustomSubmit={() => {
                const prompt = welcomeCustomText.trim()
                if (!prompt) return
                welcomeLog('custom_prompt_submit len=%d', prompt.length)
                setWelcomeCustomText('')
                startWithPrompt(prompt)
              }}
            />
          )}
          {displayMessages.map((message, index) => {
            if (message.isSystemNotification) {
            return (
              <div key={message.id} className="flex items-center justify-center gap-1.5 py-1">
                <svg className="w-3 h-3 text-text-muted/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className="text-xs font-medium text-text-muted/50">{message.content}</span>
              </div>
            )
          }
          // Hide Director header when previous non-system message was also from the Director
          const prevMsg = displayMessages.slice(0, index).reverse().find(m => !m.isSystemNotification)
          const showHeader = message.role !== 'assistant' || !prevMsg || prevMsg.role !== 'assistant'
          // Editing cannot preserve the structured skill token yet; leaving the
          // old position attached to changed text would render it incorrectly.
          const canEditMessage = status !== 'running'
            && message.role === 'user'
            && !message.selected_skill
          return (
            <MessageBubble
              key={message.id}
              message={message}
              projectId={projectId}
              canvasNodes={canvasNodes}
              mentionCandidates={messageMentionCandidates}
              onMentionClick={onMentionClick}
              onDelete={status !== 'running' ? () => deleteMessage(message.timestamp, message.content) : undefined}
              onEdit={canEditMessage
                ? (newContent: string, referencedNodeIds: string[]) => editMessage(
                    message.timestamp,
                    message.content,
                    newContent,
                    referencedNodeIds,
                  )
                : undefined}
              showHeader={showHeader}
            />
          )
        })}

        <ReplayOperationCards
          enabled={activeReplayScenario === 'dashboard-video-editor'}
          projectId={projectId}
          canvasNodes={replayCanvasNodes ?? canvasNodes}
          canvasEdges={canvasEdges}
          onCreateCanvas={onReplayCreateCanvas}
          onDeleteCanvas={onReplayDeleteCanvas}
          onFocusNode={onMentionClick}
          onCardsUpdated={handleReplayCardsUpdated}
        />

        {/* Current questions (embedded from messages) */}
        {visibleQuestions.length > 0 && <div ref={questionsStartRef} />}
        {visibleQuestions.map((question, index) => {
          const key = getAnswerKey(question)
          const isSubmitting = submittingQuestionIds.has(key)
          const isMultiQuestion = visibleQuestions.length > 1

          if (pageableQuestions.length > 1 && isPageableClarificationQuestion(question)) {
            if (question !== pageableQuestions[0]) return null
            return (
              <BatchedQuestionCard
                key={pageableQuestions.map(getAnswerKey).join(':')}
                questions={pageableQuestions.map((pageableQuestion) => {
                  const questionKey = getAnswerKey(pageableQuestion)
                  return {
                    id: questionKey,
                    question: pageableQuestion.question,
                    context: pageableQuestion.context,
                    options: pageableQuestion.options,
                    nodeRef: pageableQuestion.metadata?.node_ref,
                    byomConfiguration: pageableQuestion.metadata?.byom_configuration,
                    selectedOption: selectedAnswers[questionKey],
                    allowCustomInput: pageableQuestion.allow_custom_input,
                    customText: customTexts[questionKey] || '',
                    skipped: skippedQuestionIds.has(questionKey),
                  }
                })}
                onSelect={(questionId, option) => {
                  trackChatCard(projectId, 'batched_question', 'select', { questionCount: pageableQuestions.length })
                  handleOptionSelect(questionId, option, true)
                }}
                onCustomTextChange={handleCustomTextChange}
                onCustomInputFocus={handleCustomInputFocus}
                onSkip={(...args: Parameters<typeof handleBatchedQuestionSkip>) => {
                  trackChatCard(projectId, 'batched_question', 'skip', { questionCount: pageableQuestions.length })
                  return handleBatchedQuestionSkip(...args)
                }}
                onSubmit={(...args: Parameters<typeof handleBatchedQuestionsSubmit>) => {
                  trackChatCard(projectId, 'batched_question', 'submit', { questionCount: pageableQuestions.length })
                  return handleBatchedQuestionsSubmit(...args)
                }}
                disabled={status === 'running'
                  && pageableQuestions.some((item) => submittingQuestionIds.has(getAnswerKey(item)))}
                canvasNodes={canvasNodes}
              />
            )
          }

          // Cost confirmation: render ExecutionPlanCard with node details
          if (question.tool_use_id?.startsWith('__cost_confirmation__') && question.metadata?.nodes) {
            const billingMode: 'paid' | 'free_trial' = question.metadata.billing_mode === 'free_trial' ? 'free_trial' : 'paid'
            const nodes = parseExecutionPlanNodes(question.metadata.nodes).map((node) => ({
              ...node,
              billingMode,
            }))
            const totalSparks = typeof question.metadata.payable_sparks === 'number'
              ? question.metadata.payable_sparks
              : question.metadata.total_sparks ?? 0
            return (
              <ExecutionPlanCard
                key={key}
                nodes={nodes}
                totalSparks={totalSparks}
                selectedOption={selectedAnswers[key]}
                onSelect={(option) => {
                  trackExecutionPlan(projectId, option, nodes.length, totalSparks)
                  handleOptionSelect(key, option)
                }}
                onNodeClick={onMentionClick}
                disabled={isSubmitting}
              />
            )
          }

          // Library reuse: render the visual library picker. Selecting an item
          // answers the question with its node_id, which routes back to the
          // Director (peer of ask_user_question) so it can use_from_library +
          // wire the item per the conversation.
          if (question.metadata?.render_as === 'library_picker' && question.metadata.items) {
            return (
              <LibraryPickerCard
                key={key}
                question={question.question}
                items={question.metadata.items}
                selectedNodeId={selectedAnswers[key]}
                onSelect={(nodeId) => {
                  trackChatCard(projectId, 'library_picker', 'select', { optionCount: question.metadata?.items?.length })
                  handleOptionSelect(key, nodeId)
                }}
                disabled={isSubmitting}
              />
            )
          }

          // Sequel / new-project proposal: the Director surfaces a "Create new
          // project" button. The user must click to proceed — the Director
          // never creates autonomously. On click, we create the project +
          // navigate with deterministic pending actions (library imports) that
          // fire before the new Director's first turn.
          if (question.metadata?.render_as === 'create_project_button' && question.metadata.create_project) {
            const cpMeta = question.metadata.create_project
            return (
              <CreateProjectCard
                key={key}
                name={cpMeta.name}
                carryoverCount={cpMeta.library_node_ids?.length ?? 0}
                onCreate={() => {
                  trackChatCard(projectId, 'create_project', 'create')
                  return handleCreateProjectFromProposal(key, cpMeta)
                }}
                onDismiss={() => {
                  trackChatCard(projectId, 'create_project', 'dismiss')
                  handleOptionSelect(key, 'cancel')
                }}
                disabled={isSubmitting}
              />
            )
          }

          // Once-per-project setup confirmation (feature: project-setup-card).
          // Confirm answers the question; "Open global config" / body click
          // only open the modal, so the user can edit first and confirm after.
          if (question.metadata?.render_as === 'project_setup_card') {
            return (
              <ProjectSetupCard
                key={key}
                projectId={projectId}
                canvasId={canvasId}
                autoConfirmCostSettings={autoConfirmCostSettings}
                onConfirm={() => {
                  trackChatCard(projectId, 'project_setup', 'confirm')
                  handleOptionSelect(key, 'confirm')
                }}
                disabled={isSubmitting}
              />
            )
          }

          // Director-requested user-owned Global Config change (e.g. cost
          // auto-confirm). "I'm finished" answers; the Director re-reads the diff.
          if (question.metadata?.render_as === 'global_config_action_card') {
            return (
              <GlobalConfigActionCard
                key={key}
                projectId={projectId}
                canvasId={canvasId}
                reason={question.metadata.reason ?? question.question}
                settingsHint={question.metadata.settings_hint ?? []}
                autoConfirmCostSettings={autoConfirmCostSettings}
                onFinished={() => {
                  trackChatCard(projectId, 'global_config', 'finished')
                  handleOptionSelect(key, 'finished')
                }}
                disabled={isSubmitting}
              />
            )
          }

          if (
            question.metadata?.render_as === 'voice_catalogue_action_card'
            && question.metadata.selection_context
          ) {
            return (
              <VoiceCatalogueActionCard
                key={key}
                question={question.question}
                selectionContext={question.metadata.selection_context}
                onApplied={(answer) => {
                  trackChatCard(projectId, 'voice_catalogue', 'apply')
                  return handleStructuredQuestionAnswer(key, answer)
                }}
                disabled={isSubmitting}
              />
            )
          }

          if (question.metadata?.render_as === 'style_choice_card') {
            return (
              <StyleChoiceCard
                key={key}
                projectId={projectId}
                canvasId={canvasId}
                question={question.question}
                options={question.options}
                onAnswer={(answer) => {
                  trackChatCard(projectId, 'style_choice', 'select', { optionCount: question.options?.length })
                  handleOptionSelect(key, answer)
                }}
                disabled={isSubmitting}
              />
            )
          }

          return (
            <QuestionCard
              key={key}
              ref={(el) => {
                if (el) {
                  questionRefs.current.set(key, el)
                } else {
                  questionRefs.current.delete(key)
                }
              }}
              question={question.question}
              context={question.context}
              options={question.options}
              nodeRef={question.metadata?.node_ref}
              byomConfiguration={question.metadata?.byom_configuration}
              selectedOption={selectedAnswers[key]}
              onSelect={(option) => {
                trackChatCard(projectId, 'question', 'select', { optionCount: question.options?.length })
                handleOptionSelect(key, option)
              }}
              disabled={isSubmitting}
              allowCustomInput={question.allow_custom_input}
              customText={customTexts[key] || ''}
              onCustomTextChange={(text) => handleCustomTextChange(key, text)}
              onCustomInputFocus={() => handleCustomInputFocus(key)}
              onCustomSubmit={() => {
                trackChatCard(projectId, 'question', 'custom_submit', { optionCount: question.options?.length })
                return handleCustomSubmit(key)
              }}
              autoSubmit={true}
              isMultiQuestion={isMultiQuestion}
              questionIndex={index}
              // Post 1585245219899442: gives the "Or type your own..." field
              // the same @mention affordance as the composer above. Only the
              // canvas-backed chat passes this; landing/published surfaces
              // leave it undefined and keep the plain input.
              canvasNodes={canvasNodes}
              onSelectionMade={() => {
                // Scroll to next question if there is one
                if (index < visibleQuestions.length - 1) {
                  setTimeout(() => {
                    const nextKey = getAnswerKey(visibleQuestions[index + 1])
                    questionRefs.current.get(nextKey)?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center'
                    })
                  }, 200)
                }
              }}
            />
          )
        })}

        {/* Running indicator — only when Director is actively thinking/streaming,
            not when only worker nodes are generating (RFC-0289) */}
        {showDirectorIndicator && visibleQuestions.length === 0 && !messages.some(message => message.presentation?.status === 'running') && (
          <div className="text-base font-medium">
            <DirectorThought />
          </div>
        )}

        <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Floating scroll-to-bottom button */}
        {isUserScrolledUp && (
          <button
            type="button"
            data-testid="scroll-to-bottom-button"
            onClick={() => {
              isUserScrolledUpRef.current = false
              setIsUserScrolledUp(false)
              scrollToBottom()
            }}
            className="absolute bottom-3 right-4 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-surface-secondary/80 border border-white/10 shadow-lg backdrop-blur-sm text-text-muted hover:text-text-primary hover:bg-surface-secondary transition-all duration-200"
            title={t('agentChat.scrollToBottom')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>

      {/* Running/queued tasks indicator — shows count of non-director nodes currently executing or queued */}
      {hasTaskActivity && (
        <div
          data-testid="running-tasks-card"
          className="flex-shrink-0 mx-3 mb-2 p-3 bg-chrome/10 border border-chrome/30 rounded-md"
        >
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-chrome opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-chrome" />
            </span>
            <span className="text-sm text-chrome">
              {runningTaskCount > 0 && queuedTaskCount > 0
                ? `${t('agentChat.countRunning', { count: runningTaskCount })}, ${t('agentChat.countQueued', { count: queuedTaskCount })}`
                : runningTaskCount > 0
                  ? t('agentChat.taskRunning', { count: runningTaskCount })
                  : t('agentChat.taskQueued', { count: queuedTaskCount })
              }
            </span>
          </div>
        </div>
      )}

      {/* Execution Plan Stack — reads from YDoc, independent of chat messages */}
      {executionPlanBatches.length > 0 && onConfirmBatch && onCancelBatch && (
        <ExecutionPlanStack
          batches={executionPlanBatches}
          onConfirm={onConfirmBatch}
          onCancel={onCancelBatch}
          onNodeClick={onMentionClick}
        />
      )}

      {/* Chat socket drop. Neutral on purpose, however long it lasts: the
          Director keeps running server-side and the reconnect snapshot
          reconciles the chat. A dropped socket never shows the error card —
          that is reserved for failures the backend reports. */}
      {showReconnectingIndicator && (
        <div
          data-testid="chat-reconnecting-indicator"
          role="status"
          aria-live="polite"
          className="flex-shrink-0 mx-3 mb-2 px-3 py-2 flex items-center gap-2 rounded-md border border-border-subtle bg-surface-0 text-sm text-text-muted"
        >
          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-chrome flex-shrink-0" />
          <span>{t('agentChat.reconnecting', { appName: APP_NAME })}</span>
        </div>
      )}

      {/* Error card - show whenever there's an error, regardless of status */}
      {error && !dismissedError && (
        <div data-testid="chat-error-banner" className="flex-shrink-0 mx-3 mb-2 p-3 bg-red-500/20 border border-red-500/50 rounded-md">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1 text-base text-red-400">{error}</div>
            <button
              onClick={() => setDismissedError(true)}
              className="text-red-400/60 hover:text-red-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Queued prompts stack — shown between error card and input */}
      {queuedPrompts.length > 0 && (
        <div
          className="flex-shrink-0 mx-3 mb-2 rounded-xl border border-border-subtle bg-surface-0 p-2 space-y-2"
          data-testid="queued-prompts-stack"
          aria-live="polite"
        >
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('agentChat.queued', { count: queuedPrompts.length })}
            </div>
            <div className="text-xs font-medium text-text-muted">
              {t('agentChat.queuedHint')}
            </div>
          </div>
          {queuedPrompts.map((qp, i) => {
            const inlineMentions = renderInlineMessageTokens(
              qp.message,
              messageMentionCandidates,
              projectId,
              onMentionClick,
              t('agentChat.focusOnCanvas'),
              t('agentChat.removedNodeChip'),
              qp.selectedSkill
                ? {
                    ref: qp.selectedSkill.ref,
                    version_id: qp.selectedSkill.versionId,
                    name: qp.selectedSkill.name,
                    public_slug: qp.selectedSkill.publicSlug,
                    display_name: qp.selectedSkill.displayName,
                    position: qp.selectedSkillPosition ?? 0,
                  }
                : undefined,
            )
            return (
              <div
                key={qp.id}
                data-testid={`queued-prompt-${i}`}
                className="flex items-center gap-2 rounded-lg border border-input-border bg-input-bg px-3 py-2 text-sm text-foreground"
              >
                <span className="flex-1 min-w-0 truncate">
                  {inlineMentions ?? qp.message}
                </span>
                <div className="flex-shrink-0 flex items-center gap-1">
                <button
                  type="button"
                  data-testid={`queued-prompt-edit-${i}`}
                  onClick={() => {
                    trackInteraction('chat_queued_prompt_action', { project_id: projectId, action: 'edit' })
                    editQueuedPrompt(i)
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome/50"
                  title={t('agentChat.editInTextField')}
                  aria-label={t('agentChat.editInTextField')}
                >
                  <Pencil className="w-4 h-4" strokeWidth={2} data-icon-name="pencil" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  data-testid={`queued-prompt-send-${i}`}
                  onClick={() => {
                    trackInteraction('chat_queued_prompt_action', { project_id: projectId, action: 'send' })
                    sendQueuedPrompt(i)
                  }}
                  disabled={isDirectorActive}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-chrome focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome/50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-input-border disabled:hover:bg-surface-1"
                  title={t('agentChat.sendNow')}
                  aria-label={t('agentChat.sendNow')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-testid={`queued-prompt-remove-${i}`}
                  onClick={() => {
                    trackInteraction('chat_queued_prompt_action', { project_id: projectId, action: 'remove' })
                    removeQueuedPrompt(i)
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-destructive/50 hover:bg-destructive/20 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/50"
                  title={t('agentChat.deleteQueuedMessage')}
                  aria-label={t('agentChat.deleteQueuedMessage')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            )
          })}
        </div>
      )}

      {/* Chat input with status indicator */}
      {/* RFC 0061: Added onStop for stop button */}
      <ChatInput
        ref={chatInputRef}
        deskDraftKey={`${projectId}:${canvasId}`}
        onSend={handleSendMessage}
        onRetrieveQueuedPrompt={retrieveNewestQueuedPrompt}
        onStop={handleStop}
        projectId={projectId}
        canvasNodes={canvasNodes}
        onMentionClick={onMentionClick}
        onUploadMedia={onUploadMedia}
        placeholder={
          hasRealQuestions
            ? t('agentChat.placeholderSkipQuestions')
            : isDirectorActive
              ? t('agentChat.placeholderSendMessage')
              : error
                ? t('agentChat.placeholderRetry')
                : messages.length > 0
                  ? t('agentChat.placeholderContinue')
                  : t('agentChat.placeholderStart')
        }
        status={shouldQueuePrompt ? 'running' : 'idle'}
      />
    </div>
    </MentionRenderProvider>
  )
})

/** Clean AI-generated text by replacing em-dashes with more natural alternatives */
function cleanAiText(text: string): string {
  return text
    .replace(/\s*—\s*/g, ', ')  // em-dash with optional spaces -> comma
    .replace(/\s*–\s*/g, ', ')  // en-dash -> comma
}

/** Regex matching inline mention markers like @[[nodeId]] */
const MENTION_MARKER_RE = /@\[\[([^\]]+)\]\]/g
const SKILL_MARKER_RE = /\[\[\/([a-z0-9][a-z0-9_-]{0,63})\]\]/g

/**
 * Render message content with inline mention chips at their original positions.
 *
 * Splits the content around @[[nodeId]] markers and interleaves plain-text
 * spans with clickable mention chip buttons.  Falls back to null when the
 * content has no markers (caller should use the legacy "chips-first" path).
 */
function renderInlineMessageTokens(
  content: string,
  candidates: readonly MentionCandidate[],
  projectId: string,
  onMentionClick: ((nodeId: string) => void) | undefined,
  focusLabel: string,
  removedLabel: string,
  selectedSkill?: AgentChatMessage['selected_skill'],
): React.ReactNode | null {
  type InlineToken =
    | { kind: 'mention'; index: number; length: number; nodeId: string }
    | { kind: 'skill'; index: number; length: number }

  MENTION_MARKER_RE.lastIndex = 0
  const tokens: InlineToken[] = []
  let markerMatch: RegExpExecArray | null
  while ((markerMatch = MENTION_MARKER_RE.exec(content)) !== null) {
    // `@` immediately before a skill token is literal user text, not a node
    // mention. Leave it in the text stream so the structured skill marker can
    // render independently at the following character.
    if (markerMatch[1].startsWith('/')) continue
    tokens.push({
      kind: 'mention',
      index: markerMatch.index,
      length: markerMatch[0].length,
      nodeId: markerMatch[1],
    })
  }
  let foundSelectedSkillMarker = false
  if (selectedSkill) {
    SKILL_MARKER_RE.lastIndex = 0
    const matchingSkillMarkers: Array<{ index: number; length: number }> = []
    const skillMarkerNames = new Set([selectedSkill.public_slug, selectedSkill.name])
    let skillMatch: RegExpExecArray | null
    while ((skillMatch = SKILL_MARKER_RE.exec(content)) !== null) {
      if (!skillMarkerNames.has(skillMatch[1])) continue
      matchingSkillMarkers.push({ index: skillMatch.index, length: skillMatch[0].length })
    }
    // A message has exactly one structured skill selection. Prefer its
    // persisted offset when duplicate marker text exists, then fall back to
    // the first matching marker for clients that omitted the optional offset.
    const selectedMarker = matchingSkillMarkers.find(
      (marker) => marker.index === selectedSkill.position,
    ) ?? matchingSkillMarkers[0]
    if (selectedMarker) {
      tokens.push({ kind: 'skill', ...selectedMarker })
      foundSelectedSkillMarker = true
    }
  }
  // Backward compatibility for messages persisted before skill markers were
  // serialized into content. Their metadata contains only an inline offset.
  if (selectedSkill && !foundSelectedSkillMarker) {
    tokens.push({
      kind: 'skill',
      index: Math.min(Math.max(selectedSkill.position, 0), content.length),
      length: 0,
    })
  }
  if (tokens.length === 0) return null
  tokens.sort((left, right) => left.index - right.index || (left.kind === 'skill' ? -1 : 1))

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  for (const token of tokens) {
    if (token.index < lastIndex) continue
    if (token.index > lastIndex) {
      parts.push(
        <span key={`t-${lastIndex}-${token.index}-${parts.length}`}>
          {content.slice(lastIndex, token.index)}
        </span>,
      )
    }

    if (token.kind === 'skill' && selectedSkill) {
      parts.push(
        <span
          key={`s-${selectedSkill.ref}-${token.index}`}
          data-testid="chat-message-skill-chip"
          data-skill-ref={selectedSkill.ref}
          data-skill-version-id={selectedSkill.version_id}
          className="mx-1 inline-flex max-w-full items-center gap-1.5 rounded-md border border-input-border bg-surface-1 px-2 py-0.5 text-sm text-foreground align-baseline"
        >
          <WandSparkles className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
          <span className="truncate">{selectedSkill.display_name}</span>
        </span>,
      )
      lastIndex = token.index + token.length
      continue
    }

    if (token.kind !== 'mention') continue
    const candidate = resolveMentionCandidate(token.nodeId, candidates, removedLabel)
    parts.push(
      <ReadOnlyMentionChip
        key={`m-${token.nodeId}-${token.index}`}
        candidate={candidate}
        projectId={projectId}
        testId={`chat-message-reference-chip-${token.nodeId}`}
        onActivate={onMentionClick}
        title={focusLabel}
      />,
    )
    lastIndex = token.index + token.length
  }

  // Remaining text after last marker
  if (lastIndex < content.length) {
    parts.push(<span key={`tail-${lastIndex}`}>{content.slice(lastIndex)}</span>)
  }

  return <>{parts}</>
}

// Legacy alias for backward compatibility
const stripDagProposals = stripInternalBlocks

// Trash icon for delete button (Lucide Trash2)
function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  )
}

// Pencil icon for edit indicator
function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

// Message component
function MessageBubble({
  message,
  projectId,
  canvasNodes,
  mentionCandidates,
  onMentionClick,
  onDelete,
  onEdit,
  showHeader = true,
}: {
  message: AgentChatMessage
  projectId: string
  canvasNodes?: ChatCanvasNode[]
  mentionCandidates: readonly MentionCandidate[]
  onMentionClick?: (nodeId: string) => void
  onDelete?: () => void
  onEdit?: (newContent: string, referencedNodeIds: string[]) => void
  showHeader?: boolean
}) {
  const isUser = message.role === 'user'
  const [isEditing, setIsEditing] = useState(false)
  const editComposerRef = useRef<InlineMentionComposerHandle>(null)
  const editStateRef = useRef<InlineMentionComposerState>({ plainText: '', referencedNodeIds: [] })
  const { t } = useTranslation('workspace')
  const hasInlineMentionMarkers = /@\[\[[^\]]+\]\]/.test(message.content)

  // Hoisted ABOVE the `isUser` early return below — a `useMemo` placed after it
  // would run for Director messages only, changing hook order between the two
  // message kinds (react-hooks/rules-of-hooks).
  //
  // Both branches wanted the identical expression, so one memo serves both: the
  // user branch computed it inline and the Director branch fed it to the
  // markdown boundary. `cleanAiText` showed 56ms of self time in the pan
  // profile, so it is worth not repeating per render.
  const cleanContent = useMemo(
    () => {
      const stripped = stripDagProposals(message.content)
      return isUser ? stripped : cleanAiText(stripped)
    },
    [isUser, message.content],
  )

  useEffect(() => {
    if (!isUser || (!hasInlineMentionMarkers && !message.referenced_node_ids?.length)) return
    mentionChipRenderLog({
      event: 'message-chip-render',
      message_id: message.id,
      source: hasInlineMentionMarkers ? 'inline-marker' : 'legacy-referenced-node-ids',
      renderer: 'shared-read-only-mention-chip',
      read_only: true,
      reference_count: message.referenced_node_ids?.length ?? 0,
    })
  }, [hasInlineMentionMarkers, isUser, message.id, message.referenced_node_ids])

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing) {
      // Defer focus to after the composer mounts and hydrates
      requestAnimationFrame(() => editComposerRef.current?.focus())
    }
  }, [isEditing])

  const loggedAnsweredQuestionIdsRef = useRef(new Set<string>())
  useEffect(() => {
    for (const question of message.questions ?? []) {
      if (isSkippedTranscriptQuestion(question)) {
        const logKey = `${question.tool_use_id ?? question.question}:skipped`
        if (loggedAnsweredQuestionIdsRef.current.has(logKey)) continue
        loggedAnsweredQuestionIdsRef.current.add(logKey)
        questionCardLog({
          event: 'question-card:skipped_rendered',
          toolUseId: question.tool_use_id ?? '',
          questionType: question.question_type,
          optionCount: question.options.length,
        })
        continue
      }
      const presentation = answeredQuestionPresentation(question)
      if (!presentation) continue
      const customAnswer = presentation.kind === 'custom'
      const logKey = `${question.tool_use_id ?? question.question}:${presentation.answer.length}:${customAnswer}`
      if (loggedAnsweredQuestionIdsRef.current.has(logKey)) continue
      loggedAnsweredQuestionIdsRef.current.add(logKey)
      questionCardLog({
        event: 'question-card:answered_render_classified',
        toolUseId: question.tool_use_id ?? '',
        answerKind: customAnswer ? 'custom' : 'preset',
        answerLength: presentation.answer.length,
        optionCount: question.options.length,
        customInputAllowed: !!question.allow_custom_input,
      })
    }
  }, [message.questions])

  const answeredPageableQuestions = (message.questions ?? []).filter(
    isPageableClarificationQuestion,
  )
  const showAnsweredBatch = answeredPageableQuestions.length > 1
    && answeredPageableQuestions.every(hasCompleteAnsweredQuestionPresentation)

  // Build initialContent for the edit composer: if the message uses legacy
  // referenced_node_ids (no @[[]] markers in content), prepend markers so the
  // composer can hydrate them into chips.
  const editInitialContent = useMemo(() => {
    // RFC-1218: strip attached-document payloads first. The bubble already
    // hides them, but hydrating the raw content would drop a whole extracted
    // document into the edit box — and re-sending would then post the
    // paperclip line as literal text instead of the document.
    const editable = stripInternalBlocks(message.content ?? '')
    if (hasInlineMentionMarkers || !message.referenced_node_ids?.length) return editable
    const prefix = message.referenced_node_ids.map((id) => `@[[${id}]]`).join(' ')
    return `${prefix} ${editable}`
  }, [hasInlineMentionMarkers, message.content, message.referenced_node_ids])

  const handleDoubleClick = useCallback(() => {
    if (!onEdit) return
    setIsEditing(true)
  }, [onEdit])

  const handleEditSubmit = useCallback(() => {
    const { plainText, referencedNodeIds } = editStateRef.current
    const trimmed = plainText.trim()
    if (!trimmed || !onEdit) {
      setIsEditing(false)
      return
    }
    setIsEditing(false)
    onEdit(trimmed, referencedNodeIds)
  }, [onEdit])

  const handleEditCancel = useCallback(() => {
    setIsEditing(false)
  }, [])

  const deleteButton = onDelete ? (
    <button
      type="button"
      data-testid="delete-message-button"
      onClick={onDelete}
      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-white/10 text-text-muted hover:text-red-400"
      title={t('agentChat.deleteMessage')}
    >
      <TrashIcon className="w-3.5 h-3.5" />
    </button>
  ) : null

  if (isUser) {
    const cleanUserContent = cleanContent
    // User messages: bubble style
    return (
      <div className="group flex justify-end gap-1 items-start">
        {deleteButton}
        <div
          className={`relative max-w-[85%] px-3 py-2 text-base leading-relaxed text-chrome bg-chrome/10 border border-chrome/20 rounded-lg rounded-br-sm ${onEdit ? 'cursor-pointer' : ''}`}
          onDoubleClick={handleDoubleClick}
          data-testid="user-message-bubble"
        >
          {isEditing ? (
            <div className="flex flex-col gap-1">
              <div
                data-testid="edit-message-composer"
                className="w-full bg-transparent border border-white/20 rounded text-base leading-relaxed text-text-primary focus-within:border-chrome/50"
              >
                <InlineMentionComposer
                  ref={editComposerRef}
                  canvasNodes={canvasNodes}
                  initialContent={editInitialContent}
                  placeholder={t('agentChat.editMessagePlaceholder')}
                  onContentChange={(state) => { editStateRef.current = state }}
                  onSubmit={handleEditSubmit}
                  onEscape={handleEditCancel}
                  onMentionClick={onMentionClick}
                />
              </div>
              <div className="flex justify-end gap-1 text-xs text-text-muted">
                <span>{t('agentChat.enterToSave')}</span>
                <span className="mx-0.5">|</span>
                <span>{t('agentChat.escToCancel')}</span>
              </div>
            </div>
          ) : (
            <div className="break-words whitespace-pre-wrap text-left">
              {(() => {
                // New path: content contains @[[nodeId]] markers — render chips inline
                const inlineRendered = renderInlineMessageTokens(
                  cleanUserContent,
                  mentionCandidates,
                  projectId,
                  onMentionClick,
                  t('agentChat.focusOnCanvas'),
                  t('agentChat.removedNodeChip'),
                  message.selected_skill,
                )
                if (inlineRendered) return inlineRendered

                // Legacy path: no markers in content — render chips first, then text
                return (
                  <>
                    {message.referenced_node_ids && message.referenced_node_ids.length > 0 &&
                      Array.from(new Set(message.referenced_node_ids)).map((nodeId) => {
                        const candidate = resolveMentionCandidate(nodeId, mentionCandidates, t('agentChat.removedNodeChip'))
                        return (
                          <ReadOnlyMentionChip
                            key={nodeId}
                            candidate={candidate}
                            projectId={projectId}
                            testId={`chat-message-reference-chip-${nodeId}`}
                            onActivate={onMentionClick}
                            title={t('agentChat.focusOnCanvas')}
                          />
                        )
                      })}
                    {cleanUserContent && <span>{cleanUserContent}</span>}
                  </>
                )
              })()}
            </div>
          )}
          {!isEditing && onEdit && (
            <PencilIcon className="absolute bottom-2 right-2 w-3 h-3 text-text-muted opacity-0 group-hover:opacity-50 transition-opacity" />
          )}
        </div>
      </div>
    )
  }

  // Director messages: Georgia serif with typewriter effect.
  // `cleanContent` is memoized at the top of the component (see the note there
  // on why it cannot live here).

  return (
    <div className="group space-y-1.5">
      {/* Director label with icon + delete button — hidden for consecutive Director messages */}
      {showHeader && (
        <div className="flex items-center gap-1.5">
          {/* Crescent brandmark — same mask trick as the sidebar chip, so the
              per-message header logo tracks the top-bar brandmark. Both are
              `chrome` now; the logo PNG carries only an alpha channel, so
              whatever paints through the mask IS the logo's colour. */}
          <span
            aria-hidden
            className="inline-block w-4 h-4 bg-chrome flex-shrink-0"
            style={{
              WebkitMaskImage: "url('/landing-v2/logo-icon.png')",
              maskImage: "url('/landing-v2/logo-icon.png')",
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }}
          />
          <span
            className="text-sm font-bold text-chrome"
            data-testid="agent-chat-dreamwrite-label"
          >
            {import.meta.env.DEV && import.meta.env.VITE_DIRECTOR_PROVIDER === 'claude-code'
              ? t('agentChat.directorClaudeCode')
              : t('agentChat.director')}
          </span>
          {deleteButton}
        </div>
      )}
      {!showHeader && deleteButton && (
        <div className="flex justify-end">{deleteButton}</div>
      )}

      {/* Message content. Was the warm-neutral #9d947c, chosen to sit against
          an amber-tinted sidebar wash that no longer exists. `chrome/75` is the
          Figma H2 rung (#FFFFFF at 75%, frame 167:91) — the body weight of the
          agent text, one step under the #FFFFFF used for emphasis. */}
      {message.presentation && <DirectorTurnActivity presentation={message.presentation} />}
      {message.metadata?.render_as === 'action_card' && <ActionCard metadata={message.metadata} cardId={message.id} />}
      {message.actionCardError && <p role="alert" className="text-sm text-destructive">{t('chat:actionCard.invalid')}</p>}
      {cleanContent && (
        <div className="text-base leading-relaxed text-chrome/75 break-words">
          <MemoizedMarkdown content={cleanContent} />
        </div>
      )}

      {message.activity?.state === 'in_progress' && (
        <div
          role="status"
          aria-live="polite"
          data-testid="chat-message-progress-indicator"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground"
        >
          <span className="relative flex h-2 w-2" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-chrome opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-chrome" />
          </span>
          <span>{t('agentChat.inProgress')}</span>
        </div>
      )}

      {showAnsweredBatch && (
        <BatchedQuestionCard
          questions={answeredPageableQuestions.map((question) => {
            const presentation = answeredQuestionPresentation(question)
            const skipped = question.answer === '__skipped__'
            return {
              id: getAnswerKey(question),
              question: question.question,
              context: question.context,
              options: question.options,
              nodeRef: question.metadata?.node_ref,
              byomConfiguration: question.metadata?.byom_configuration,
              selectedOption: presentation?.kind === 'preset' ? presentation.selectedOption : undefined,
              allowCustomInput: question.allow_custom_input,
              customText: presentation?.kind === 'custom' ? presentation.answer : '',
              customSelected: presentation?.kind === 'custom',
              skipped,
            }
          })}
          disabled
          canvasNodes={canvasNodes}
        />
      )}

      {/* Answered cost confirmation: persist the execution plan card inline */}
      {message.questions?.map((q, qIdx) => {
        const presentation = answeredQuestionPresentation(q)
        if (showAnsweredBatch && isPageableClarificationQuestion(q)) return null
        if (!presentation && isSkippedTranscriptQuestion(q)) {
          return (
            <div key={q.tool_use_id || qIdx}>
              <QuestionCard
                question={q.question}
                context={q.context}
                options={q.options}
                byomConfiguration={q.metadata?.byom_configuration}
                nodeRef={q.metadata?.node_ref}
                selectedOption={undefined}
                onSelect={noop}
                allowCustomInput={q.allow_custom_input}
                customText=""
                customSelected={false}
                disabled
                forceListLayout
                canvasNodes={canvasNodes}
              />
              <div
                role="status"
                data-testid="question-card-skipped-status"
                className="px-1 pb-1 text-sm text-muted-foreground"
              >
                {t('chat:questionBatch.skipped')}
              </div>
            </div>
          )
        }
        if (!presentation) return null
        const rawAnswer = presentation.answer

        // Cost confirmation → ExecutionPlanCard
        if (q.tool_use_id?.startsWith('__cost_confirmation__') && q.metadata?.nodes) {
          const billingMode: 'paid' | 'free_trial' = q.metadata.billing_mode === 'free_trial' ? 'free_trial' : 'paid'
          const nodes = parseExecutionPlanNodes(q.metadata.nodes).map((node) => ({
            ...node,
            billingMode,
          }))
          const totalSparks = typeof q.metadata.payable_sparks === 'number'
            ? q.metadata.payable_sparks
            : q.metadata.total_sparks ?? 0
          return (
            <ExecutionPlanCard
              key={q.tool_use_id}
              nodes={nodes}
              totalSparks={totalSparks}
              selectedOption={rawAnswer}
              onSelect={() => {}}
              onNodeClick={onMentionClick}
              disabled
            />
          )
        }

        // Answered config cards → compact read-only transcript entry. A typed
        // (custom) reply is shown as the detail line.
        if (q.metadata?.render_as === 'project_setup_card' || q.metadata?.render_as === 'global_config_action_card') {
          const isSetupCard = q.metadata.render_as === 'project_setup_card'
          return (
            <AnsweredConfigCard
              key={q.tool_use_id || qIdx}
              testId={isSetupCard ? 'project-setup-card-answered' : 'global-config-action-card-answered'}
              label={isSetupCard ? t('chat:projectSetupCard.confirmed') : t('chat:globalConfigActionCard.answered')}
              detail={presentation.kind === 'custom' ? rawAnswer : undefined}
            />
          )
        }

        const isCustomAnswer = presentation.kind === 'custom'

        // All other answered questions → QuestionCard (read-only)
        return (
          <QuestionCard
            key={q.tool_use_id || qIdx}
            question={q.question}
            context={q.context}
            options={q.options}
            byomConfiguration={q.metadata?.byom_configuration}
            selectedOption={isCustomAnswer ? undefined : presentation.selectedOption}
            onSelect={() => {}}
            allowCustomInput={q.allow_custom_input}
            customText={isCustomAnswer ? rawAnswer : ''}
            customSelected={isCustomAnswer}
            disabled
            // Post 1585245219899442 (RFC-1168 §7): the ANSWERED card needs the
            // same mention context as the live one. Without it this read-only
            // render falls back to the plain <input> and echoes the raw
            // `@[[nodeId]]` marker into the transcript, so a user who inserted
            // a chip sees machine text back. The composer hydrates the marker
            // into a chip; `disabled` keeps it read-only.
            canvasNodes={canvasNodes}
          />
        )
      })}
    </div>
  )
}

export default AgentChat
